import React, { useState, useEffect, useRef, useMemo, FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Badge } from "reactstrap";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Tabs,
  Tag,
  Select,
  Progress,
  Tooltip,
  DatePicker,
  Alert,
  Collapse,
  Spin,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  FileTextOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FundProjectionScreenOutlined,
  AlertOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import * as echarts from "echarts";
import { formatCurrency } from "../../utils/Utils";
import budgetAnalysisDashboardApis from "../../api/master/budgetAnalysisDashboard";
import budgetApis from "../../api/master/budget";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

// ===================== DUMMY DATA =====================

const budgetSummaryByProject: Record<string, any> = {
  proj1: {
    budgetId: "BUD-2024-001",
    budgetName: "Greenfield Township - Phase 1",
    siteName: "Site A - Hyderabad",
    contractorName: "ABC Constructions",
    startDate: "01-Jan-2024",
    endDate: "31-Dec-2024",
    status: "Approved",
  },
  proj2: {
    budgetId: "BUD-2024-002",
    budgetName: "Metro Mall Construction",
    siteName: "Site B - Bangalore",
    contractorName: "LMN Builders",
    startDate: "01-Mar-2024",
    endDate: "28-Feb-2025",
    status: "Approved",
  },
  proj3: {
    budgetId: "BUD-2024-003",
    budgetName: "Highway Bridge Project",
    siteName: "Site C - Chennai",
    contractorName: "RST Infrastructure",
    startDate: "01-Feb-2024",
    endDate: "31-Jan-2025",
    status: "Approved",
  },
};

const budgetSummary = budgetSummaryByProject.proj1;

// KPI data per project
const kpiCardsByProject: Record<string, any[]> = {
  proj1: [
    { title: "Total Budget", value: 12000000000, icon: <DollarOutlined />, color: "#1890ff", trend: null },
    { title: "Total Committed", value: 7500000000, icon: <BarChartOutlined />, color: "#722ed1", trend: null, description: "PO + Work Orders" },
    { title: "Total Consumed", value: 5200000000, icon: <PieChartOutlined />, color: "#faad14", trend: null, description: "Invoices + Expenses + RA Bills" },
    { title: "Available Budget", value: 4500000000, icon: <CheckCircleOutlined />, color: "#52c41a", trend: null },
    { title: "Forecast at Completion", value: 11800000000, icon: <FundProjectionScreenOutlined />, color: "#13c2c2", trend: null },
    { title: "Budget Variance", value: 200000000, icon: <ArrowUpOutlined />, color: "#52c41a", trend: "saving" },
  ],
  proj2: [
    { title: "Total Budget", value: 8400000000, icon: <DollarOutlined />, color: "#1890ff", trend: null },
    { title: "Total Committed", value: 5250000000, icon: <BarChartOutlined />, color: "#722ed1", trend: null, description: "PO + Work Orders" },
    { title: "Total Consumed", value: 3640000000, icon: <PieChartOutlined />, color: "#faad14", trend: null, description: "Invoices + Expenses + RA Bills" },
    { title: "Available Budget", value: 3150000000, icon: <CheckCircleOutlined />, color: "#52c41a", trend: null },
    { title: "Forecast at Completion", value: 8260000000, icon: <FundProjectionScreenOutlined />, color: "#13c2c2", trend: null },
    { title: "Budget Variance", value: 140000000, icon: <ArrowUpOutlined />, color: "#52c41a", trend: "saving" },
  ],
  proj3: [
    { title: "Total Budget", value: 15600000000, icon: <DollarOutlined />, color: "#1890ff", trend: null },
    { title: "Total Committed", value: 9750000000, icon: <BarChartOutlined />, color: "#722ed1", trend: null, description: "PO + Work Orders" },
    { title: "Total Consumed", value: 6760000000, icon: <PieChartOutlined />, color: "#faad14", trend: null, description: "Invoices + Expenses + RA Bills" },
    { title: "Available Budget", value: 5850000000, icon: <CheckCircleOutlined />, color: "#52c41a", trend: null },
    { title: "Forecast at Completion", value: 15340000000, icon: <FundProjectionScreenOutlined />, color: "#13c2c2", trend: null },
    { title: "Budget Variance", value: 260000000, icon: <ArrowUpOutlined />, color: "#52c41a", trend: "saving" },
  ],
};

// Section 2: Budget Distribution by Category
const categoryDistributionData = [
  // proj1 - Greenfield Township
  { key: "1", category: "Civil Works", budget: 4000000000, committed: 3000000000, consumed: 2500000000, available: 1500000000, project: "proj1" },
  { key: "2", category: "Finishing", budget: 2500000000, committed: 1800000000, consumed: 1200000000, available: 1300000000, project: "proj1" },
  { key: "3", category: "MEP", budget: 2000000000, committed: 1200000000, consumed: 800000000, available: 1200000000, project: "proj1" },
  { key: "4", category: "External Works", budget: 1000000000, committed: 700000000, consumed: 400000000, available: 600000000, project: "proj1" },
  { key: "5", category: "Landscaping", budget: 500000000, committed: 300000000, consumed: 150000000, available: 350000000, project: "proj1" },
  // proj2 - Metro Mall Construction (70% of proj1)
  { key: "p2-1", category: "Civil Works", budget: 2800000000, committed: 2100000000, consumed: 1750000000, available: 1050000000, project: "proj2" },
  { key: "p2-2", category: "Finishing", budget: 1750000000, committed: 1260000000, consumed: 840000000, available: 910000000, project: "proj2" },
  { key: "p2-3", category: "MEP", budget: 1400000000, committed: 840000000, consumed: 560000000, available: 840000000, project: "proj2" },
  { key: "p2-4", category: "External Works", budget: 700000000, committed: 490000000, consumed: 280000000, available: 420000000, project: "proj2" },
  { key: "p2-5", category: "Landscaping", budget: 350000000, committed: 210000000, consumed: 105000000, available: 245000000, project: "proj2" },
  // proj3 - Highway Bridge Project (130% of proj1)
  { key: "p3-1", category: "Civil Works", budget: 5200000000, committed: 3900000000, consumed: 3250000000, available: 1950000000, project: "proj3" },
  { key: "p3-2", category: "Finishing", budget: 3250000000, committed: 2340000000, consumed: 1560000000, available: 1690000000, project: "proj3" },
  { key: "p3-3", category: "MEP", budget: 2600000000, committed: 1560000000, consumed: 1040000000, available: 1560000000, project: "proj3" },
  { key: "p3-4", category: "External Works", budget: 1300000000, committed: 910000000, consumed: 520000000, available: 780000000, project: "proj3" },
  { key: "p3-5", category: "Landscaping", budget: 650000000, committed: 390000000, consumed: 195000000, available: 455000000, project: "proj3" },
];

// Section 3: Budget Consumption Source Analysis
const consumptionSourceData = [
  // proj1
  { name: "Procurement (PO + GRN)", value: 3200000000, project: "proj1" },
  { name: "Work Orders (Contractors)", value: 1500000000, project: "proj1" },
  { name: "Project Task Expenses", value: 300000000, project: "proj1" },
  { name: "Petty Cash Expenses", value: 200000000, project: "proj1" },
  // proj2 (70%)
  { name: "Procurement (PO + GRN)", value: 2240000000, project: "proj2" },
  { name: "Work Orders (Contractors)", value: 1050000000, project: "proj2" },
  { name: "Project Task Expenses", value: 210000000, project: "proj2" },
  { name: "Petty Cash Expenses", value: 140000000, project: "proj2" },
  // proj3 (130%)
  { name: "Procurement (PO + GRN)", value: 4160000000, project: "proj3" },
  { name: "Work Orders (Contractors)", value: 1950000000, project: "proj3" },
  { name: "Project Task Expenses", value: 390000000, project: "proj3" },
  { name: "Petty Cash Expenses", value: 260000000, project: "proj3" },
];

// Section 4: Budget Health Indicator
const budgetHealthData = [
  // proj1
  { key: "1", category: "Civil Works", utilization: 78, status: "monitor", project: "proj1" },
  { key: "2", category: "Finishing", utilization: 60, status: "healthy", project: "proj1" },
  { key: "3", category: "MEP", utilization: 92, status: "risk", project: "proj1" },
  { key: "4", category: "External Works", utilization: 65, status: "healthy", project: "proj1" },
  { key: "5", category: "Landscaping", utilization: 45, status: "healthy", project: "proj1" },
  // proj2
  { key: "p2-1", category: "Civil Works", utilization: 72, status: "monitor", project: "proj2" },
  { key: "p2-2", category: "Finishing", utilization: 55, status: "healthy", project: "proj2" },
  { key: "p2-3", category: "MEP", utilization: 85, status: "monitor", project: "proj2" },
  { key: "p2-4", category: "External Works", utilization: 58, status: "healthy", project: "proj2" },
  { key: "p2-5", category: "Landscaping", utilization: 40, status: "healthy", project: "proj2" },
  // proj3
  { key: "p3-1", category: "Civil Works", utilization: 82, status: "monitor", project: "proj3" },
  { key: "p3-2", category: "Finishing", utilization: 65, status: "healthy", project: "proj3" },
  { key: "p3-3", category: "MEP", utilization: 95, status: "risk", project: "proj3" },
  { key: "p3-4", category: "External Works", utilization: 70, status: "monitor", project: "proj3" },
  { key: "p3-5", category: "Landscaping", utilization: 50, status: "healthy", project: "proj3" },
];

// Section 5: Budget Line Analytics
const budgetLineAnalyticsData = [
  // proj1
  { key: "1", budgetLine: "RCC Slab", contractor: "ABC Constructions", budget: 500000000, committed: 400000000, consumed: 300000000, available: 200000000, project: "proj1" },
  { key: "2", budgetLine: "Foundation", contractor: "XYZ Infra", budget: 600000000, committed: 500000000, consumed: 500000000, available: 100000000, project: "proj1" },
  { key: "3", budgetLine: "Column Casting", contractor: "ABC Constructions", budget: 400000000, committed: 350000000, consumed: 250000000, available: 150000000, project: "proj1" },
  { key: "4", budgetLine: "Brick Work", contractor: "PQR Builders", budget: 300000000, committed: 200000000, consumed: 150000000, available: 150000000, project: "proj1" },
  { key: "5", budgetLine: "Plastering", contractor: "XYZ Infra", budget: 250000000, committed: 180000000, consumed: 120000000, available: 130000000, project: "proj1" },
  // proj2
  { key: "p2-1", budgetLine: "RCC Slab", contractor: "ABC Constructions", budget: 350000000, committed: 280000000, consumed: 210000000, available: 140000000, project: "proj2" },
  { key: "p2-2", budgetLine: "Foundation", contractor: "XYZ Infra", budget: 420000000, committed: 350000000, consumed: 350000000, available: 70000000, project: "proj2" },
  { key: "p2-3", budgetLine: "Column Casting", contractor: "ABC Constructions", budget: 280000000, committed: 245000000, consumed: 175000000, available: 105000000, project: "proj2" },
  { key: "p2-4", budgetLine: "Brick Work", contractor: "PQR Builders", budget: 210000000, committed: 140000000, consumed: 105000000, available: 105000000, project: "proj2" },
  { key: "p2-5", budgetLine: "Plastering", contractor: "XYZ Infra", budget: 175000000, committed: 126000000, consumed: 84000000, available: 91000000, project: "proj2" },
  // proj3
  { key: "p3-1", budgetLine: "RCC Slab", contractor: "ABC Constructions", budget: 650000000, committed: 520000000, consumed: 390000000, available: 260000000, project: "proj3" },
  { key: "p3-2", budgetLine: "Foundation", contractor: "XYZ Infra", budget: 780000000, committed: 650000000, consumed: 650000000, available: 130000000, project: "proj3" },
  { key: "p3-3", budgetLine: "Column Casting", contractor: "ABC Constructions", budget: 520000000, committed: 455000000, consumed: 325000000, available: 195000000, project: "proj3" },
  { key: "p3-4", budgetLine: "Brick Work", contractor: "PQR Builders", budget: 390000000, committed: 260000000, consumed: 195000000, available: 195000000, project: "proj3" },
  { key: "p3-5", budgetLine: "Plastering", contractor: "XYZ Infra", budget: 325000000, committed: 234000000, consumed: 156000000, available: 169000000, project: "proj3" },
];

// Section 6: Budget vs Purchase Orders
const budgetVsPOData = [
  // proj1
  { key: "1", budgetLine: "Cement", budget: 200000000, poValue: 150000000, grnValue: 100000000, invoiceValue: 80000000, project: "proj1" },
  { key: "2", budgetLine: "Steel TMT", budget: 500000000, poValue: 400000000, grnValue: 300000000, invoiceValue: 280000000, project: "proj1" },
  { key: "3", budgetLine: "Sand & Aggregate", budget: 150000000, poValue: 120000000, grnValue: 90000000, invoiceValue: 85000000, project: "proj1" },
  { key: "4", budgetLine: "Electrical Cables", budget: 100000000, poValue: 80000000, grnValue: 60000000, invoiceValue: 55000000, project: "proj1" },
  { key: "5", budgetLine: "Plumbing Materials", budget: 80000000, poValue: 60000000, grnValue: 45000000, invoiceValue: 40000000, project: "proj1" },
  // proj2
  { key: "p2-1", budgetLine: "Cement", budget: 140000000, poValue: 105000000, grnValue: 70000000, invoiceValue: 56000000, project: "proj2" },
  { key: "p2-2", budgetLine: "Steel TMT", budget: 350000000, poValue: 280000000, grnValue: 210000000, invoiceValue: 196000000, project: "proj2" },
  { key: "p2-3", budgetLine: "Sand & Aggregate", budget: 105000000, poValue: 84000000, grnValue: 63000000, invoiceValue: 59500000, project: "proj2" },
  { key: "p2-4", budgetLine: "Electrical Cables", budget: 70000000, poValue: 56000000, grnValue: 42000000, invoiceValue: 38500000, project: "proj2" },
  { key: "p2-5", budgetLine: "Plumbing Materials", budget: 56000000, poValue: 42000000, grnValue: 31500000, invoiceValue: 28000000, project: "proj2" },
  // proj3
  { key: "p3-1", budgetLine: "Cement", budget: 260000000, poValue: 195000000, grnValue: 130000000, invoiceValue: 104000000, project: "proj3" },
  { key: "p3-2", budgetLine: "Steel TMT", budget: 650000000, poValue: 520000000, grnValue: 390000000, invoiceValue: 364000000, project: "proj3" },
  { key: "p3-3", budgetLine: "Sand & Aggregate", budget: 195000000, poValue: 156000000, grnValue: 117000000, invoiceValue: 110500000, project: "proj3" },
  { key: "p3-4", budgetLine: "Electrical Cables", budget: 130000000, poValue: 104000000, grnValue: 78000000, invoiceValue: 71500000, project: "proj3" },
  { key: "p3-5", budgetLine: "Plumbing Materials", budget: 104000000, poValue: 78000000, grnValue: 58500000, invoiceValue: 52000000, project: "proj3" },
];

// Section 7: Budget vs Work Orders
const budgetVsWOData = [
  // proj1
  { key: "1", workPackage: "RCC Works", contractor: "ABC Constructions", budget: 500000000, woValue: 450000000, raBills: 300000000, balance: 200000000, project: "proj1" },
  { key: "2", workPackage: "MEP Works", contractor: "DEF Engineering", budget: 300000000, woValue: 280000000, raBills: 180000000, balance: 120000000, project: "proj1" },
  { key: "3", workPackage: "Finishing Works", contractor: "GHI Interiors", budget: 250000000, woValue: 220000000, raBills: 150000000, balance: 100000000, project: "proj1" },
  { key: "4", workPackage: "External Development", contractor: "JKL Infra", budget: 200000000, woValue: 180000000, raBills: 100000000, balance: 100000000, project: "proj1" },
  // proj2
  { key: "p2-1", workPackage: "RCC Works", contractor: "ABC Constructions", budget: 350000000, woValue: 315000000, raBills: 210000000, balance: 140000000, project: "proj2" },
  { key: "p2-2", workPackage: "MEP Works", contractor: "DEF Engineering", budget: 210000000, woValue: 196000000, raBills: 126000000, balance: 84000000, project: "proj2" },
  { key: "p2-3", workPackage: "Finishing Works", contractor: "GHI Interiors", budget: 175000000, woValue: 154000000, raBills: 105000000, balance: 70000000, project: "proj2" },
  { key: "p2-4", workPackage: "External Development", contractor: "JKL Infra", budget: 140000000, woValue: 126000000, raBills: 70000000, balance: 70000000, project: "proj2" },
  // proj3
  { key: "p3-1", workPackage: "RCC Works", contractor: "ABC Constructions", budget: 650000000, woValue: 585000000, raBills: 390000000, balance: 260000000, project: "proj3" },
  { key: "p3-2", workPackage: "MEP Works", contractor: "DEF Engineering", budget: 390000000, woValue: 364000000, raBills: 234000000, balance: 156000000, project: "proj3" },
  { key: "p3-3", workPackage: "Finishing Works", contractor: "GHI Interiors", budget: 325000000, woValue: 286000000, raBills: 195000000, balance: 130000000, project: "proj3" },
  { key: "p3-4", workPackage: "External Development", contractor: "JKL Infra", budget: 260000000, woValue: 234000000, raBills: 130000000, balance: 130000000, project: "proj3" },
];

// Section 8: Budget vs Project Tasks
const budgetVsTasksData = [
  // proj1
  { key: "1", task: "Foundation", budget: 300000000, spent: 200000000, balance: 100000000, project: "proj1" },
  { key: "2", task: "Column Casting", budget: 200000000, spent: 100000000, balance: 100000000, project: "proj1" },
  { key: "3", task: "Slab Casting", budget: 400000000, spent: 280000000, balance: 120000000, project: "proj1" },
  { key: "4", task: "Brick Work", budget: 250000000, spent: 150000000, balance: 100000000, project: "proj1" },
  { key: "5", task: "Plastering", budget: 180000000, spent: 80000000, balance: 100000000, project: "proj1" },
  // proj2
  { key: "p2-1", task: "Foundation", budget: 210000000, spent: 140000000, balance: 70000000, project: "proj2" },
  { key: "p2-2", task: "Column Casting", budget: 140000000, spent: 70000000, balance: 70000000, project: "proj2" },
  { key: "p2-3", task: "Slab Casting", budget: 280000000, spent: 196000000, balance: 84000000, project: "proj2" },
  { key: "p2-4", task: "Brick Work", budget: 175000000, spent: 105000000, balance: 70000000, project: "proj2" },
  { key: "p2-5", task: "Plastering", budget: 126000000, spent: 56000000, balance: 70000000, project: "proj2" },
  // proj3
  { key: "p3-1", task: "Foundation", budget: 390000000, spent: 260000000, balance: 130000000, project: "proj3" },
  { key: "p3-2", task: "Column Casting", budget: 260000000, spent: 130000000, balance: 130000000, project: "proj3" },
  { key: "p3-3", task: "Slab Casting", budget: 520000000, spent: 364000000, balance: 156000000, project: "proj3" },
  { key: "p3-4", task: "Brick Work", budget: 325000000, spent: 195000000, balance: 130000000, project: "proj3" },
  { key: "p3-5", task: "Plastering", budget: 234000000, spent: 104000000, balance: 130000000, project: "proj3" },
];

// Section 9: Expense Budget Analysis
const expenseBudgetData = [
  // proj1
  { key: "1", expenseType: "Diesel", budget: 2000000, actual: 1500000, project: "proj1" },
  { key: "2", expenseType: "Site Office", budget: 1000000, actual: 700000, project: "proj1" },
  { key: "3", expenseType: "Travel", budget: 500000, actual: 300000, project: "proj1" },
  { key: "4", expenseType: "Safety Equipment", budget: 800000, actual: 650000, project: "proj1" },
  { key: "5", expenseType: "Miscellaneous", budget: 300000, actual: 200000, project: "proj1" },
  // proj2
  { key: "p2-1", expenseType: "Diesel", budget: 1400000, actual: 1050000, project: "proj2" },
  { key: "p2-2", expenseType: "Site Office", budget: 700000, actual: 490000, project: "proj2" },
  { key: "p2-3", expenseType: "Travel", budget: 350000, actual: 210000, project: "proj2" },
  { key: "p2-4", expenseType: "Safety Equipment", budget: 560000, actual: 455000, project: "proj2" },
  { key: "p2-5", expenseType: "Miscellaneous", budget: 210000, actual: 140000, project: "proj2" },
  // proj3
  { key: "p3-1", expenseType: "Diesel", budget: 2600000, actual: 1950000, project: "proj3" },
  { key: "p3-2", expenseType: "Site Office", budget: 1300000, actual: 910000, project: "proj3" },
  { key: "p3-3", expenseType: "Travel", budget: 650000, actual: 390000, project: "proj3" },
  { key: "p3-4", expenseType: "Safety Equipment", budget: 1040000, actual: 845000, project: "proj3" },
  { key: "p3-5", expenseType: "Miscellaneous", budget: 390000, actual: 260000, project: "proj3" },
];

// Section 10: Contractor Cost Performance
const contractorCostData = [
  // proj1
  { key: "1", contractor: "ABC Constructions", budget: 1000000000, actual: 900000000, variance: 100000000, varianceType: "Saving", project: "proj1" },
  { key: "2", contractor: "XYZ Infra", budget: 700000000, actual: 800000000, variance: -100000000, varianceType: "Overrun", project: "proj1" },
  { key: "3", contractor: "DEF Engineering", budget: 500000000, actual: 480000000, variance: 20000000, varianceType: "Saving", project: "proj1" },
  { key: "4", contractor: "PQR Builders", budget: 400000000, actual: 420000000, variance: -20000000, varianceType: "Overrun", project: "proj1" },
  // proj2
  { key: "p2-1", contractor: "ABC Constructions", budget: 700000000, actual: 630000000, variance: 70000000, varianceType: "Saving", project: "proj2" },
  { key: "p2-2", contractor: "XYZ Infra", budget: 490000000, actual: 560000000, variance: -70000000, varianceType: "Overrun", project: "proj2" },
  { key: "p2-3", contractor: "DEF Engineering", budget: 350000000, actual: 336000000, variance: 14000000, varianceType: "Saving", project: "proj2" },
  { key: "p2-4", contractor: "PQR Builders", budget: 280000000, actual: 294000000, variance: -14000000, varianceType: "Overrun", project: "proj2" },
  // proj3
  { key: "p3-1", contractor: "ABC Constructions", budget: 1300000000, actual: 1170000000, variance: 130000000, varianceType: "Saving", project: "proj3" },
  { key: "p3-2", contractor: "XYZ Infra", budget: 910000000, actual: 1040000000, variance: -130000000, varianceType: "Overrun", project: "proj3" },
  { key: "p3-3", contractor: "DEF Engineering", budget: 650000000, actual: 624000000, variance: 26000000, varianceType: "Saving", project: "proj3" },
  { key: "p3-4", contractor: "PQR Builders", budget: 520000000, actual: 546000000, variance: -26000000, varianceType: "Overrun", project: "proj3" },
];

// Section 11: Time vs Budget Burn
const budgetBurnByProject: Record<string, any> = {
  proj1: {
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    burn: [500000000, 700000000, 600000000, 800000000, 750000000, 650000000, 0, 0, 0, 0, 0, 0],
    cumulative: [500000000, 1200000000, 1800000000, 2600000000, 3350000000, 4000000000, 0, 0, 0, 0, 0, 0],
  },
  proj2: {
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    burn: [350000000, 490000000, 420000000, 560000000, 525000000, 455000000, 0, 0, 0, 0, 0, 0],
    cumulative: [350000000, 840000000, 1260000000, 1820000000, 2345000000, 2800000000, 0, 0, 0, 0, 0, 0],
  },
  proj3: {
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    burn: [650000000, 910000000, 780000000, 1040000000, 975000000, 845000000, 0, 0, 0, 0, 0, 0],
    cumulative: [650000000, 1560000000, 2340000000, 3380000000, 4355000000, 5200000000, 0, 0, 0, 0, 0, 0],
  },
};

const budgetBurnData = budgetBurnByProject.proj1;

// Section 13: Budget Variance Analysis
const varianceAnalysisData = [
  // proj1
  { key: "1", category: "Civil Works", budget: 4000000000, forecast: 4200000000, variance: -200000000, project: "proj1" },
  { key: "2", category: "MEP", budget: 2000000000, forecast: 1800000000, variance: 200000000, project: "proj1" },
  { key: "3", category: "Finishing", budget: 2500000000, forecast: 2400000000, variance: 100000000, project: "proj1" },
  { key: "4", category: "External Works", budget: 1000000000, forecast: 1050000000, variance: -50000000, project: "proj1" },
  // proj2
  { key: "p2-1", category: "Civil Works", budget: 2800000000, forecast: 2940000000, variance: -140000000, project: "proj2" },
  { key: "p2-2", category: "MEP", budget: 1400000000, forecast: 1260000000, variance: 140000000, project: "proj2" },
  { key: "p2-3", category: "Finishing", budget: 1750000000, forecast: 1680000000, variance: 70000000, project: "proj2" },
  { key: "p2-4", category: "External Works", budget: 700000000, forecast: 735000000, variance: -35000000, project: "proj2" },
  // proj3
  { key: "p3-1", category: "Civil Works", budget: 5200000000, forecast: 5460000000, variance: -260000000, project: "proj3" },
  { key: "p3-2", category: "MEP", budget: 2600000000, forecast: 2340000000, variance: 260000000, project: "proj3" },
  { key: "p3-3", category: "Finishing", budget: 3250000000, forecast: 3120000000, variance: 130000000, project: "proj3" },
  { key: "p3-4", category: "External Works", budget: 1300000000, forecast: 1365000000, variance: -65000000, project: "proj3" },
];

// Section 14: Budget Control Alerts
const budgetAlerts = [
  // proj1
  { key: "1", type: "Over Budget", category: "MEP - HVAC", message: "Consumed (₹18.5Cr) exceeds Budget (₹18Cr)", severity: "error", project: "proj1" },
  { key: "2", type: "Over Commitment", category: "Civil - RCC", message: "Committed + Consumed (₹48Cr) exceeds Budget (₹45Cr)", severity: "error", project: "proj1" },
  { key: "3", type: "Budget Near Exhaustion", category: "Finishing - Painting", message: "Consumed is 88% of budget", severity: "warning", project: "proj1" },
  { key: "4", type: "Budget Near Exhaustion", category: "MEP - Electrical", message: "Consumed is 85% of budget", severity: "warning", project: "proj1" },
  { key: "5", type: "Over Budget", category: "External - Landscaping", message: "Consumed (₹5.2Cr) exceeds Budget (₹5Cr)", severity: "error", project: "proj1" },
  // proj2
  { key: "p2-1", type: "Over Budget", category: "MEP - HVAC", message: "Consumed (₹12.5Cr) exceeds Budget (₹12Cr)", severity: "error", project: "proj2" },
  { key: "p2-2", type: "Budget Near Exhaustion", category: "Civil - Foundation", message: "Consumed is 90% of budget", severity: "warning", project: "proj2" },
  { key: "p2-3", type: "Over Commitment", category: "Finishing - Flooring", message: "Committed + Consumed (₹15Cr) exceeds Budget (₹14Cr)", severity: "error", project: "proj2" },
  { key: "p2-4", type: "Budget Near Exhaustion", category: "MEP - Plumbing", message: "Consumed is 82% of budget", severity: "warning", project: "proj2" },
  { key: "p2-5", type: "Over Budget", category: "External - Parking", message: "Consumed (₹3.8Cr) exceeds Budget (₹3.5Cr)", severity: "error", project: "proj2" },
  // proj3
  { key: "p3-1", type: "Over Budget", category: "Civil - Piling", message: "Consumed (₹25Cr) exceeds Budget (₹24Cr)", severity: "error", project: "proj3" },
  { key: "p3-2", type: "Over Commitment", category: "MEP - Electrical", message: "Committed + Consumed (₹60Cr) exceeds Budget (₹55Cr)", severity: "error", project: "proj3" },
  { key: "p3-3", type: "Budget Near Exhaustion", category: "Civil - Girder Work", message: "Consumed is 91% of budget", severity: "warning", project: "proj3" },
  { key: "p3-4", type: "Budget Near Exhaustion", category: "External - Road Approach", message: "Consumed is 87% of budget", severity: "warning", project: "proj3" },
  { key: "p3-5", type: "Over Budget", category: "Finishing - Railing", message: "Consumed (₹7Cr) exceeds Budget (₹6.5Cr)", severity: "error", project: "proj3" },
];

// Section 12: Forecast Cost at Completion
const forecastData = [
  // proj1
  { key: "1", category: "Civil Works", budget: 4000000000, consumed: 2500000000, committed: 1000000000, forecastAtCompletion: 3800000000, variance: 200000000, project: "proj1" },
  { key: "2", category: "MEP", budget: 2000000000, consumed: 800000000, committed: 500000000, forecastAtCompletion: 1900000000, variance: 100000000, project: "proj1" },
  { key: "3", category: "Finishing", budget: 2500000000, consumed: 1200000000, committed: 800000000, forecastAtCompletion: 2400000000, variance: 100000000, project: "proj1" },
  { key: "4", category: "External", budget: 1000000000, consumed: 400000000, committed: 300000000, forecastAtCompletion: 1050000000, variance: -50000000, project: "proj1" },
  { key: "5", category: "Landscaping", budget: 500000000, consumed: 150000000, committed: 100000000, forecastAtCompletion: 450000000, variance: 50000000, project: "proj1" },
  // proj2
  { key: "p2-1", category: "Civil Works", budget: 2800000000, consumed: 1750000000, committed: 700000000, forecastAtCompletion: 2660000000, variance: 140000000, project: "proj2" },
  { key: "p2-2", category: "MEP", budget: 1400000000, consumed: 560000000, committed: 350000000, forecastAtCompletion: 1330000000, variance: 70000000, project: "proj2" },
  { key: "p2-3", category: "Finishing", budget: 1750000000, consumed: 840000000, committed: 560000000, forecastAtCompletion: 1680000000, variance: 70000000, project: "proj2" },
  { key: "p2-4", category: "External", budget: 700000000, consumed: 280000000, committed: 210000000, forecastAtCompletion: 735000000, variance: -35000000, project: "proj2" },
  { key: "p2-5", category: "Landscaping", budget: 350000000, consumed: 105000000, committed: 70000000, forecastAtCompletion: 315000000, variance: 35000000, project: "proj2" },
  // proj3
  { key: "p3-1", category: "Civil Works", budget: 5200000000, consumed: 3250000000, committed: 1300000000, forecastAtCompletion: 4940000000, variance: 260000000, project: "proj3" },
  { key: "p3-2", category: "MEP", budget: 2600000000, consumed: 1040000000, committed: 650000000, forecastAtCompletion: 2470000000, variance: 130000000, project: "proj3" },
  { key: "p3-3", category: "Finishing", budget: 3250000000, consumed: 1560000000, committed: 1040000000, forecastAtCompletion: 3120000000, variance: 130000000, project: "proj3" },
  { key: "p3-4", category: "External", budget: 1300000000, consumed: 520000000, committed: 390000000, forecastAtCompletion: 1365000000, variance: -65000000, project: "proj3" },
  { key: "p3-5", category: "Landscaping", budget: 650000000, consumed: 195000000, committed: 130000000, forecastAtCompletion: 585000000, variance: 65000000, project: "proj3" },
];

// ---- Report Data ----

// Report 3: Budget Consumption by Contractor
const contractorConsumptionReportData = [
  // proj1
  { key: "1", contractor: "ABC Infra", budgetAllocated: 1000000000, woValue: 900000000, billsSubmitted: 700000000, paidAmount: 600000000, remainingBudget: 300000000, project: "proj1" },
  { key: "2", contractor: "XYZ Constructions", budgetAllocated: 800000000, woValue: 600000000, billsSubmitted: 400000000, paidAmount: 400000000, remainingBudget: 400000000, project: "proj1" },
  { key: "3", contractor: "DEF Engineering", budgetAllocated: 500000000, woValue: 450000000, billsSubmitted: 350000000, paidAmount: 300000000, remainingBudget: 150000000, project: "proj1" },
  // proj2
  { key: "p2-1", contractor: "ABC Infra", budgetAllocated: 700000000, woValue: 630000000, billsSubmitted: 490000000, paidAmount: 420000000, remainingBudget: 210000000, project: "proj2" },
  { key: "p2-2", contractor: "XYZ Constructions", budgetAllocated: 560000000, woValue: 420000000, billsSubmitted: 280000000, paidAmount: 280000000, remainingBudget: 280000000, project: "proj2" },
  { key: "p2-3", contractor: "DEF Engineering", budgetAllocated: 350000000, woValue: 315000000, billsSubmitted: 245000000, paidAmount: 210000000, remainingBudget: 105000000, project: "proj2" },
  // proj3
  { key: "p3-1", contractor: "ABC Infra", budgetAllocated: 1300000000, woValue: 1170000000, billsSubmitted: 910000000, paidAmount: 780000000, remainingBudget: 390000000, project: "proj3" },
  { key: "p3-2", contractor: "XYZ Constructions", budgetAllocated: 1040000000, woValue: 780000000, billsSubmitted: 520000000, paidAmount: 520000000, remainingBudget: 520000000, project: "proj3" },
  { key: "p3-3", contractor: "DEF Engineering", budgetAllocated: 650000000, woValue: 585000000, billsSubmitted: 455000000, paidAmount: 390000000, remainingBudget: 195000000, project: "proj3" },
];

// Report 4: Budget vs PO Report
const budgetVsPOReportData = [
  // proj1
  { key: "1", budgetLine: "Cement", budget: 200000000, prValue: 180000000, poValue: 150000000, grnValue: 100000000, invoiceValue: 80000000, remaining: 120000000, project: "proj1" },
  { key: "2", budgetLine: "Steel", budget: 500000000, prValue: 400000000, poValue: 300000000, grnValue: 200000000, invoiceValue: 180000000, remaining: 320000000, project: "proj1" },
  { key: "3", budgetLine: "Electrical Cables", budget: 100000000, prValue: 90000000, poValue: 75000000, grnValue: 50000000, invoiceValue: 45000000, remaining: 55000000, project: "proj1" },
  // proj2
  { key: "p2-1", budgetLine: "Cement", budget: 140000000, prValue: 126000000, poValue: 105000000, grnValue: 70000000, invoiceValue: 56000000, remaining: 84000000, project: "proj2" },
  { key: "p2-2", budgetLine: "Steel", budget: 350000000, prValue: 280000000, poValue: 210000000, grnValue: 140000000, invoiceValue: 126000000, remaining: 224000000, project: "proj2" },
  { key: "p2-3", budgetLine: "Electrical Cables", budget: 70000000, prValue: 63000000, poValue: 52500000, grnValue: 35000000, invoiceValue: 31500000, remaining: 38500000, project: "proj2" },
  // proj3
  { key: "p3-1", budgetLine: "Cement", budget: 260000000, prValue: 234000000, poValue: 195000000, grnValue: 130000000, invoiceValue: 104000000, remaining: 156000000, project: "proj3" },
  { key: "p3-2", budgetLine: "Steel", budget: 650000000, prValue: 520000000, poValue: 390000000, grnValue: 260000000, invoiceValue: 234000000, remaining: 416000000, project: "proj3" },
  { key: "p3-3", budgetLine: "Electrical Cables", budget: 130000000, prValue: 117000000, poValue: 97500000, grnValue: 65000000, invoiceValue: 58500000, remaining: 71500000, project: "proj3" },
];

// Report 5: Budget vs Expenses
const budgetVsExpensesReportData = [
  // proj1
  { key: "1", expenseCategory: "Diesel", budget: 2000000, expenseRequests: 1500000, approved: 1400000, paid: 1200000, balance: 800000, project: "proj1" },
  { key: "2", expenseCategory: "Travel", budget: 500000, expenseRequests: 400000, approved: 400000, paid: 300000, balance: 200000, project: "proj1" },
  { key: "3", expenseCategory: "Site Office", budget: 1000000, expenseRequests: 800000, approved: 750000, paid: 600000, balance: 400000, project: "proj1" },
  { key: "4", expenseCategory: "Safety Equipment", budget: 800000, expenseRequests: 700000, approved: 650000, paid: 500000, balance: 300000, project: "proj1" },
  // proj2
  { key: "p2-1", expenseCategory: "Diesel", budget: 1400000, expenseRequests: 1050000, approved: 980000, paid: 840000, balance: 560000, project: "proj2" },
  { key: "p2-2", expenseCategory: "Travel", budget: 350000, expenseRequests: 280000, approved: 280000, paid: 210000, balance: 140000, project: "proj2" },
  { key: "p2-3", expenseCategory: "Site Office", budget: 700000, expenseRequests: 560000, approved: 525000, paid: 420000, balance: 280000, project: "proj2" },
  { key: "p2-4", expenseCategory: "Safety Equipment", budget: 560000, expenseRequests: 490000, approved: 455000, paid: 350000, balance: 210000, project: "proj2" },
  // proj3
  { key: "p3-1", expenseCategory: "Diesel", budget: 2600000, expenseRequests: 1950000, approved: 1820000, paid: 1560000, balance: 1040000, project: "proj3" },
  { key: "p3-2", expenseCategory: "Travel", budget: 650000, expenseRequests: 520000, approved: 520000, paid: 390000, balance: 260000, project: "proj3" },
  { key: "p3-3", expenseCategory: "Site Office", budget: 1300000, expenseRequests: 1040000, approved: 975000, paid: 780000, balance: 520000, project: "proj3" },
  { key: "p3-4", expenseCategory: "Safety Equipment", budget: 1040000, expenseRequests: 910000, approved: 845000, paid: 650000, balance: 390000, project: "proj3" },
];

// Report 6: Budget vs Work Orders
const budgetVsWOReportData = [
  // proj1
  { key: "1", workPackage: "RCC Slab", contractor: "ABC", budget: 500000000, woValue: 450000000, billsSubmitted: 300000000, paid: 200000000, balance: 200000000, project: "proj1" },
  { key: "2", workPackage: "Foundation", contractor: "XYZ", budget: 600000000, woValue: 500000000, billsSubmitted: 400000000, paid: 300000000, balance: 200000000, project: "proj1" },
  { key: "3", workPackage: "MEP Works", contractor: "DEF", budget: 300000000, woValue: 280000000, billsSubmitted: 200000000, paid: 150000000, balance: 100000000, project: "proj1" },
  // proj2
  { key: "p2-1", workPackage: "RCC Slab", contractor: "ABC", budget: 350000000, woValue: 315000000, billsSubmitted: 210000000, paid: 140000000, balance: 140000000, project: "proj2" },
  { key: "p2-2", workPackage: "Foundation", contractor: "XYZ", budget: 420000000, woValue: 350000000, billsSubmitted: 280000000, paid: 210000000, balance: 140000000, project: "proj2" },
  { key: "p2-3", workPackage: "MEP Works", contractor: "DEF", budget: 210000000, woValue: 196000000, billsSubmitted: 140000000, paid: 105000000, balance: 70000000, project: "proj2" },
  // proj3
  { key: "p3-1", workPackage: "RCC Slab", contractor: "ABC", budget: 650000000, woValue: 585000000, billsSubmitted: 390000000, paid: 260000000, balance: 260000000, project: "proj3" },
  { key: "p3-2", workPackage: "Foundation", contractor: "XYZ", budget: 780000000, woValue: 650000000, billsSubmitted: 520000000, paid: 390000000, balance: 260000000, project: "proj3" },
  { key: "p3-3", workPackage: "MEP Works", contractor: "DEF", budget: 390000000, woValue: 364000000, billsSubmitted: 260000000, paid: 195000000, balance: 130000000, project: "proj3" },
];

// Report 7: Budget Utilization Trend
const utilizationTrendReportData = [
  // proj1
  { key: "1", month: "January", budgetConsumed: 300000000, project: "proj1" },
  { key: "2", month: "February", budgetConsumed: 500000000, project: "proj1" },
  { key: "3", month: "March", budgetConsumed: 600000000, project: "proj1" },
  { key: "4", month: "April", budgetConsumed: 700000000, project: "proj1" },
  { key: "5", month: "May", budgetConsumed: 750000000, project: "proj1" },
  { key: "6", month: "June", budgetConsumed: 650000000, project: "proj1" },
  // proj2
  { key: "p2-1", month: "January", budgetConsumed: 210000000, project: "proj2" },
  { key: "p2-2", month: "February", budgetConsumed: 350000000, project: "proj2" },
  { key: "p2-3", month: "March", budgetConsumed: 420000000, project: "proj2" },
  { key: "p2-4", month: "April", budgetConsumed: 490000000, project: "proj2" },
  { key: "p2-5", month: "May", budgetConsumed: 525000000, project: "proj2" },
  { key: "p2-6", month: "June", budgetConsumed: 455000000, project: "proj2" },
  // proj3
  { key: "p3-1", month: "January", budgetConsumed: 390000000, project: "proj3" },
  { key: "p3-2", month: "February", budgetConsumed: 650000000, project: "proj3" },
  { key: "p3-3", month: "March", budgetConsumed: 780000000, project: "proj3" },
  { key: "p3-4", month: "April", budgetConsumed: 910000000, project: "proj3" },
  { key: "p3-5", month: "May", budgetConsumed: 975000000, project: "proj3" },
  { key: "p3-6", month: "June", budgetConsumed: 845000000, project: "proj3" },
];

// Report 1: Project Budget vs Actual
const projectBudgetVsActualReportData = [
  // proj1
  { key: "1", budgetCategory: "Civil Works", budget: 4000000000, committed: 3000000000, consumed: 2500000000, available: 1500000000, forecast: 3800000000, variance: 200000000, utilization: 62, project: "proj1" },
  { key: "2", budgetCategory: "Finishing", budget: 2500000000, committed: 1800000000, consumed: 1200000000, available: 1300000000, forecast: 2400000000, variance: 100000000, utilization: 48, project: "proj1" },
  { key: "3", budgetCategory: "MEP", budget: 2000000000, committed: 1200000000, consumed: 800000000, available: 1200000000, forecast: 1900000000, variance: 100000000, utilization: 40, project: "proj1" },
  { key: "4", budgetCategory: "External Works", budget: 1000000000, committed: 700000000, consumed: 400000000, available: 600000000, forecast: 1050000000, variance: -50000000, utilization: 40, project: "proj1" },
  // proj2
  { key: "p2-1", budgetCategory: "Civil Works", budget: 2800000000, committed: 2100000000, consumed: 1750000000, available: 1050000000, forecast: 2660000000, variance: 140000000, utilization: 63, project: "proj2" },
  { key: "p2-2", budgetCategory: "Finishing", budget: 1750000000, committed: 1260000000, consumed: 840000000, available: 910000000, forecast: 1680000000, variance: 70000000, utilization: 48, project: "proj2" },
  { key: "p2-3", budgetCategory: "MEP", budget: 1400000000, committed: 840000000, consumed: 560000000, available: 840000000, forecast: 1330000000, variance: 70000000, utilization: 40, project: "proj2" },
  { key: "p2-4", budgetCategory: "External Works", budget: 700000000, committed: 490000000, consumed: 280000000, available: 420000000, forecast: 735000000, variance: -35000000, utilization: 40, project: "proj2" },
  // proj3
  { key: "p3-1", budgetCategory: "Civil Works", budget: 5200000000, committed: 3900000000, consumed: 3250000000, available: 1950000000, forecast: 4940000000, variance: 260000000, utilization: 63, project: "proj3" },
  { key: "p3-2", budgetCategory: "Finishing", budget: 3250000000, committed: 2340000000, consumed: 1560000000, available: 1690000000, forecast: 3120000000, variance: 130000000, utilization: 48, project: "proj3" },
  { key: "p3-3", budgetCategory: "MEP", budget: 2600000000, committed: 1560000000, consumed: 1040000000, available: 1560000000, forecast: 2470000000, variance: 130000000, utilization: 40, project: "proj3" },
  { key: "p3-4", budgetCategory: "External Works", budget: 1300000000, committed: 910000000, consumed: 520000000, available: 780000000, forecast: 1365000000, variance: -65000000, utilization: 40, project: "proj3" },
];

// Report 8: Forecast at Completion
const forecastReportData = [
  // proj1
  { key: "1", category: "Civil Works", budget: 4000000000, consumed: 2500000000, committed: 1000000000, forecast: 3800000000, variance: 200000000, project: "proj1" },
  { key: "2", category: "MEP", budget: 2000000000, consumed: 800000000, committed: 500000000, forecast: 1900000000, variance: 100000000, project: "proj1" },
  { key: "3", category: "Finishing", budget: 2500000000, consumed: 1200000000, committed: 800000000, forecast: 2400000000, variance: 100000000, project: "proj1" },
  { key: "4", category: "External", budget: 1000000000, consumed: 400000000, committed: 300000000, forecast: 1050000000, variance: -50000000, project: "proj1" },
  // proj2
  { key: "p2-1", category: "Civil Works", budget: 2800000000, consumed: 1750000000, committed: 700000000, forecast: 2660000000, variance: 140000000, project: "proj2" },
  { key: "p2-2", category: "MEP", budget: 1400000000, consumed: 560000000, committed: 350000000, forecast: 1330000000, variance: 70000000, project: "proj2" },
  { key: "p2-3", category: "Finishing", budget: 1750000000, consumed: 840000000, committed: 560000000, forecast: 1680000000, variance: 70000000, project: "proj2" },
  { key: "p2-4", category: "External", budget: 700000000, consumed: 280000000, committed: 210000000, forecast: 735000000, variance: -35000000, project: "proj2" },
  // proj3
  { key: "p3-1", category: "Civil Works", budget: 5200000000, consumed: 3250000000, committed: 1300000000, forecast: 4940000000, variance: 260000000, project: "proj3" },
  { key: "p3-2", category: "MEP", budget: 2600000000, consumed: 1040000000, committed: 650000000, forecast: 2470000000, variance: 130000000, project: "proj3" },
  { key: "p3-3", category: "Finishing", budget: 3250000000, consumed: 1560000000, committed: 1040000000, forecast: 3120000000, variance: 130000000, project: "proj3" },
  { key: "p3-4", category: "External", budget: 1300000000, consumed: 520000000, committed: 390000000, forecast: 1365000000, variance: -65000000, project: "proj3" },
];

// Filter options
const projectOptions = [
  { value: "all", label: "All Projects" },
  { value: "proj1", label: "Greenfield Township - Phase 1" },
  { value: "proj2", label: "Metro Mall Construction" },
  { value: "proj3", label: "Highway Bridge Project" },
];

const contractorOptions = [
  { value: "all", label: "All Contractors" },
  { value: "abc", label: "ABC Constructions" },
  { value: "xyz", label: "XYZ Infra" },
  { value: "def", label: "DEF Engineering" },
  { value: "pqr", label: "PQR Builders" },
];

const categoryOptions = [
  { value: "all", label: "All Categories" },
  { value: "civil", label: "Civil Works" },
  { value: "mep", label: "MEP" },
  { value: "finishing", label: "Finishing" },
  { value: "external", label: "External Works" },
  { value: "landscaping", label: "Landscaping" },
];

const budgetLineOptions = [
  { value: "all", label: "All Budget Lines" },
  { value: "rcc", label: "RCC Slab" },
  { value: "foundation", label: "Foundation" },
  { value: "cement", label: "Cement" },
  { value: "steel", label: "Steel TMT" },
  { value: "electrical", label: "Electrical Cables" },
];

const budgetVersionOptions = [
  { value: "v3", label: "Version 3 (Current)" },
  { value: "v2", label: "Version 2" },
  { value: "v1", label: "Version 1 (Original)" },
];

const transactionTypeOptions = [
  { value: "all", label: "All Transactions" },
  { value: "po", label: "Purchase Order" },
  { value: "wo", label: "Work Order" },
  { value: "expense", label: "Expense" },
  { value: "invoice", label: "Invoice" },
];

// ===================== DRILLDOWN DATA =====================
// Level 2: Budget Lines under each Category
const drilldownBudgetLines: Record<string, any[]> = {
  "Civil Works": [
    { key: "cl-1", budgetLine: "RCC Slab", contractor: "ABC Constructions", budget: 1500000000, committed: 1200000000, consumed: 1000000000, available: 500000000 },
    { key: "cl-2", budgetLine: "Foundation", contractor: "XYZ Infra", budget: 1200000000, committed: 1000000000, consumed: 800000000, available: 400000000 },
    { key: "cl-3", budgetLine: "Column Casting", contractor: "ABC Constructions", budget: 800000000, committed: 500000000, consumed: 400000000, available: 400000000 },
    { key: "cl-4", budgetLine: "Brick Work", contractor: "PQR Builders", budget: 500000000, committed: 300000000, consumed: 300000000, available: 200000000 },
  ],
  "Finishing": [
    { key: "fl-1", budgetLine: "Plastering", contractor: "GHI Interiors", budget: 800000000, committed: 600000000, consumed: 400000000, available: 400000000 },
    { key: "fl-2", budgetLine: "Painting", contractor: "GHI Interiors", budget: 700000000, committed: 500000000, consumed: 350000000, available: 350000000 },
    { key: "fl-3", budgetLine: "Flooring", contractor: "XYZ Infra", budget: 1000000000, committed: 700000000, consumed: 450000000, available: 550000000 },
  ],
  "MEP": [
    { key: "ml-1", budgetLine: "Electrical Wiring", contractor: "DEF Engineering", budget: 800000000, committed: 500000000, consumed: 350000000, available: 450000000 },
    { key: "ml-2", budgetLine: "Plumbing", contractor: "DEF Engineering", budget: 600000000, committed: 350000000, consumed: 250000000, available: 350000000 },
    { key: "ml-3", budgetLine: "HVAC", contractor: "DEF Engineering", budget: 600000000, committed: 350000000, consumed: 200000000, available: 400000000 },
  ],
  "External Works": [
    { key: "el-1", budgetLine: "Road Work", contractor: "JKL Infra", budget: 500000000, committed: 350000000, consumed: 200000000, available: 300000000 },
    { key: "el-2", budgetLine: "Drainage", contractor: "JKL Infra", budget: 500000000, committed: 350000000, consumed: 200000000, available: 300000000 },
  ],
  "Landscaping": [
    { key: "ll-1", budgetLine: "Garden Development", contractor: "PQR Builders", budget: 300000000, committed: 200000000, consumed: 100000000, available: 200000000 },
    { key: "ll-2", budgetLine: "Hardscaping", contractor: "PQR Builders", budget: 200000000, committed: 100000000, consumed: 50000000, available: 150000000 },
  ],
};

// Level 3: Transactions under each Budget Line
const drilldownTransactions: Record<string, any[]> = {
  "RCC Slab": [
    { key: "t-1", txnId: "PO-1045", type: "Purchase Order", vendor: "ABC Constructions", amount: 450000000, date: "15-Jan-2024", status: "Completed" },
    { key: "t-2", txnId: "WO-2034", type: "Work Order", vendor: "ABC Constructions", amount: 350000000, date: "20-Feb-2024", status: "In Progress" },
    { key: "t-3", txnId: "EXP-0078", type: "Expense", vendor: "Site Office", amount: 50000000, date: "10-Mar-2024", status: "Approved" },
  ],
  "Foundation": [
    { key: "t-4", txnId: "PO-1050", type: "Purchase Order", vendor: "XYZ Infra", amount: 500000000, date: "05-Jan-2024", status: "Completed" },
    { key: "t-5", txnId: "WO-2040", type: "Work Order", vendor: "XYZ Infra", amount: 300000000, date: "15-Jan-2024", status: "Completed" },
  ],
  "Column Casting": [
    { key: "t-6", txnId: "PO-1060", type: "Purchase Order", vendor: "ABC Constructions", amount: 300000000, date: "01-Mar-2024", status: "In Progress" },
  ],
  "Brick Work": [
    { key: "t-20", txnId: "PO-1080", type: "Purchase Order", vendor: "PQR Builders", amount: 200000000, date: "15-Mar-2024", status: "In Progress" },
    { key: "t-21", txnId: "WO-2060", type: "Work Order", vendor: "PQR Builders", amount: 150000000, date: "20-Mar-2024", status: "In Progress" },
  ],
  "Slab Casting": [
    { key: "t-22", txnId: "PO-1085", type: "Purchase Order", vendor: "ABC Constructions", amount: 280000000, date: "10-Feb-2024", status: "Completed" },
    { key: "t-23", txnId: "WO-2065", type: "Work Order", vendor: "ABC Constructions", amount: 200000000, date: "25-Feb-2024", status: "In Progress" },
  ],
  "Plastering": [
    { key: "t-7", txnId: "WO-2050", type: "Work Order", vendor: "GHI Interiors", amount: 400000000, date: "10-Apr-2024", status: "In Progress" },
  ],
  "Painting": [
    { key: "t-24", txnId: "WO-2070", type: "Work Order", vendor: "GHI Interiors", amount: 350000000, date: "15-May-2024", status: "In Progress" },
  ],
  "Flooring": [
    { key: "t-25", txnId: "PO-1090", type: "Purchase Order", vendor: "XYZ Infra", amount: 450000000, date: "01-Apr-2024", status: "In Progress" },
  ],
  "Electrical Wiring": [
    { key: "t-8", txnId: "PO-1070", type: "Purchase Order", vendor: "DEF Engineering", amount: 350000000, date: "20-Feb-2024", status: "Completed" },
  ],
  "Plumbing": [
    { key: "t-26", txnId: "PO-1095", type: "Purchase Order", vendor: "DEF Engineering", amount: 250000000, date: "10-Mar-2024", status: "In Progress" },
  ],
  "HVAC": [
    { key: "t-27", txnId: "PO-1100", type: "Purchase Order", vendor: "DEF Engineering", amount: 200000000, date: "20-Apr-2024", status: "In Progress" },
  ],
  "Road Work": [
    { key: "t-28", txnId: "WO-2080", type: "Work Order", vendor: "JKL Infra", amount: 350000000, date: "01-May-2024", status: "In Progress" },
  ],
  "Drainage": [
    { key: "t-29", txnId: "WO-2085", type: "Work Order", vendor: "JKL Infra", amount: 200000000, date: "15-May-2024", status: "In Progress" },
  ],
  "Garden Development": [
    { key: "t-30", txnId: "WO-2090", type: "Work Order", vendor: "PQR Builders", amount: 100000000, date: "01-Jun-2024", status: "In Progress" },
  ],
  "Hardscaping": [
    { key: "t-31", txnId: "PO-1105", type: "Purchase Order", vendor: "PQR Builders", amount: 50000000, date: "10-Jun-2024", status: "In Progress" },
  ],
};

// Level 4: Documents under each Transaction
const drilldownDocuments: Record<string, any[]> = {
  "PO-1045": [
    { key: "d-1", docId: "INV-5001", type: "Vendor Invoice", amount: 200000000, date: "15-Mar-2024", status: "Paid" },
    { key: "d-2", docId: "INV-5015", type: "Vendor Invoice", amount: 150000000, date: "20-Apr-2024", status: "Pending" },
    { key: "d-3", docId: "GRN-3001", type: "Goods Receipt", amount: 400000000, date: "28-Feb-2024", status: "Received" },
  ],
  "WO-2034": [
    { key: "d-4", docId: "RA-001", type: "RA Bill", amount: 150000000, date: "10-Apr-2024", status: "Approved" },
    { key: "d-5", docId: "RA-002", type: "RA Bill", amount: 100000000, date: "15-May-2024", status: "Submitted" },
  ],
  "PO-1050": [
    { key: "d-6", docId: "INV-5020", type: "Vendor Invoice", amount: 250000000, date: "01-Mar-2024", status: "Paid" },
    { key: "d-7", docId: "GRN-3010", type: "Goods Receipt", amount: 500000000, date: "20-Feb-2024", status: "Received" },
  ],
  "PO-1060": [
    { key: "d-8", docId: "INV-5030", type: "Vendor Invoice", amount: 150000000, date: "15-Apr-2024", status: "Pending" },
  ],
  "WO-2050": [
    { key: "d-9", docId: "RA-005", type: "RA Bill", amount: 200000000, date: "20-May-2024", status: "Submitted" },
  ],
  "PO-1070": [
    { key: "d-10", docId: "INV-5040", type: "Vendor Invoice", amount: 180000000, date: "25-Mar-2024", status: "Paid" },
    { key: "d-11", docId: "GRN-3020", type: "Goods Receipt", amount: 350000000, date: "15-Mar-2024", status: "Received" },
  ],
  "PO-1080": [
    { key: "d-12", docId: "INV-5050", type: "Vendor Invoice", amount: 100000000, date: "10-Apr-2024", status: "Pending" },
  ],
  "WO-2060": [
    { key: "d-13", docId: "RA-010", type: "RA Bill", amount: 80000000, date: "25-Apr-2024", status: "Submitted" },
  ],
  "PO-1085": [
    { key: "d-14", docId: "INV-5055", type: "Vendor Invoice", amount: 150000000, date: "20-Mar-2024", status: "Paid" },
    { key: "d-15", docId: "GRN-3025", type: "Goods Receipt", amount: 280000000, date: "10-Mar-2024", status: "Received" },
  ],
  "WO-2065": [
    { key: "d-16", docId: "RA-011", type: "RA Bill", amount: 100000000, date: "15-Apr-2024", status: "Approved" },
  ],
  "WO-2070": [
    { key: "d-17", docId: "RA-012", type: "RA Bill", amount: 180000000, date: "10-Jun-2024", status: "Submitted" },
  ],
  "PO-1090": [
    { key: "d-18", docId: "INV-5060", type: "Vendor Invoice", amount: 200000000, date: "01-May-2024", status: "Pending" },
  ],
  "PO-1095": [
    { key: "d-19", docId: "INV-5065", type: "Vendor Invoice", amount: 120000000, date: "15-Apr-2024", status: "Paid" },
  ],
  "PO-1100": [
    { key: "d-20", docId: "INV-5070", type: "Vendor Invoice", amount: 100000000, date: "20-May-2024", status: "Pending" },
  ],
  "WO-2080": [
    { key: "d-21", docId: "RA-015", type: "RA Bill", amount: 150000000, date: "01-Jun-2024", status: "Approved" },
  ],
  "WO-2085": [
    { key: "d-22", docId: "RA-016", type: "RA Bill", amount: 100000000, date: "30-May-2024", status: "Submitted" },
  ],
  "WO-2090": [
    { key: "d-23", docId: "RA-017", type: "RA Bill", amount: 50000000, date: "15-Jun-2024", status: "Submitted" },
  ],
  "PO-1105": [
    { key: "d-24", docId: "INV-5075", type: "Vendor Invoice", amount: 25000000, date: "20-Jun-2024", status: "Pending" },
  ],
  "EXP-0078": [
    { key: "d-25", docId: "VOUCHER-001", type: "Expense Voucher", amount: 50000000, date: "10-Mar-2024", status: "Paid" },
  ],
};

// Level: Drilldown for Contractor → Work Orders → Bills
const drilldownContractorWOs: Record<string, any[]> = {
  "ABC Constructions": [
    { key: "cwo-1", woId: "WO-3001", workPackage: "RCC Works", woValue: 500000000, billsSubmitted: 400000000, paid: 350000000, status: "In Progress" },
    { key: "cwo-2", woId: "WO-3005", workPackage: "Foundation", woValue: 400000000, billsSubmitted: 300000000, paid: 250000000, status: "Completed" },
  ],
  "ABC Infra": [
    { key: "cwo-1b", woId: "WO-3001", workPackage: "RCC Works", woValue: 500000000, billsSubmitted: 400000000, paid: 350000000, status: "In Progress" },
    { key: "cwo-2b", woId: "WO-3005", workPackage: "Foundation", woValue: 400000000, billsSubmitted: 300000000, paid: 250000000, status: "Completed" },
  ],
  "XYZ Infra": [
    { key: "cwo-3", woId: "WO-3010", workPackage: "MEP Works", woValue: 350000000, billsSubmitted: 250000000, paid: 200000000, status: "In Progress" },
    { key: "cwo-4", woId: "WO-3012", workPackage: "Finishing", woValue: 250000000, billsSubmitted: 150000000, paid: 200000000, status: "In Progress" },
  ],
  "XYZ Constructions": [
    { key: "cwo-3b", woId: "WO-3010", workPackage: "MEP Works", woValue: 350000000, billsSubmitted: 250000000, paid: 200000000, status: "In Progress" },
    { key: "cwo-4b", woId: "WO-3012", workPackage: "Finishing", woValue: 250000000, billsSubmitted: 150000000, paid: 200000000, status: "In Progress" },
  ],
  "DEF Engineering": [
    { key: "cwo-5", woId: "WO-3020", workPackage: "Electrical Works", woValue: 450000000, billsSubmitted: 350000000, paid: 300000000, status: "In Progress" },
  ],
  "PQR Builders": [
    { key: "cwo-6", woId: "WO-3030", workPackage: "Brick Work", woValue: 200000000, billsSubmitted: 150000000, paid: 100000000, status: "In Progress" },
    { key: "cwo-7", woId: "WO-3035", workPackage: "Landscaping", woValue: 150000000, billsSubmitted: 80000000, paid: 60000000, status: "In Progress" },
  ],
};

const drilldownWOBills: Record<string, any[]> = {
  "WO-3001": [
    { key: "wb-1", billId: "RA-101", billDate: "15-Feb-2024", amount: 200000000, status: "Paid" },
    { key: "wb-2", billId: "RA-102", billDate: "20-Mar-2024", amount: 200000000, status: "Approved" },
  ],
  "WO-3005": [
    { key: "wb-3", billId: "RA-103", billDate: "10-Jan-2024", amount: 300000000, status: "Paid" },
  ],
  "WO-3010": [
    { key: "wb-4", billId: "RA-201", billDate: "25-Mar-2024", amount: 250000000, status: "Submitted" },
  ],
  "WO-3020": [
    { key: "wb-5", billId: "RA-301", billDate: "05-Apr-2024", amount: 180000000, status: "Paid" },
    { key: "wb-6", billId: "RA-302", billDate: "15-May-2024", amount: 170000000, status: "Approved" },
  ],
  "WO-3012": [
    { key: "wb-8", billId: "RA-202", billDate: "10-Apr-2024", amount: 150000000, status: "Approved" },
  ],
  "WO-3030": [
    { key: "wb-9", billId: "RA-401", billDate: "20-Apr-2024", amount: 80000000, status: "Paid" },
    { key: "wb-10", billId: "RA-402", billDate: "25-May-2024", amount: 70000000, status: "Submitted" },
  ],
  "WO-3035": [
    { key: "wb-11", billId: "RA-501", billDate: "01-Jun-2024", amount: 80000000, status: "Submitted" },
  ],
  "WO-5001": [
    { key: "wb-12", billId: "RA-601", billDate: "10-Mar-2024", amount: 100000000, status: "Paid" },
    { key: "wb-13", billId: "RA-602", billDate: "15-Apr-2024", amount: 80000000, status: "Approved" },
  ],
  "WO-5002": [
    { key: "wb-14", billId: "RA-603", billDate: "20-Apr-2024", amount: 120000000, status: "Submitted" },
  ],
  "WO-5010": [
    { key: "wb-15", billId: "RA-701", billDate: "15-Feb-2024", amount: 150000000, status: "Paid" },
    { key: "wb-16", billId: "RA-702", billDate: "20-Mar-2024", amount: 100000000, status: "Paid" },
  ],
  "WO-5011": [
    { key: "wb-17", billId: "RA-703", billDate: "10-Apr-2024", amount: 150000000, status: "Approved" },
  ],
  "WO-5020": [
    { key: "wb-18", billId: "RA-801", billDate: "01-May-2024", amount: 200000000, status: "Submitted" },
  ],
  "WO-5030": [
    { key: "wb-19", billId: "RA-901", billDate: "15-May-2024", amount: 150000000, status: "Approved" },
  ],
  "WO-5040": [
    { key: "wb-20", billId: "RA-1001", billDate: "01-Jun-2024", amount: 100000000, status: "Submitted" },
  ],
};

// Drilldown for Budget Line → PR → PO → GRN → Invoice
const drilldownBudgetLinePRs: Record<string, any[]> = {
  "Cement": [
    { key: "pr-1", prId: "PR-2001", prDate: "05-Jan-2024", amount: 100000000, poId: "PO-4001", poValue: 90000000, grnValue: 60000000, invoiceValue: 50000000, status: "Completed" },
    { key: "pr-2", prId: "PR-2005", prDate: "20-Feb-2024", amount: 80000000, poId: "PO-4005", poValue: 60000000, grnValue: 40000000, invoiceValue: 30000000, status: "In Progress" },
  ],
  "Steel TMT": [
    { key: "pr-3", prId: "PR-2010", prDate: "10-Jan-2024", amount: 200000000, poId: "PO-4010", poValue: 180000000, grnValue: 120000000, invoiceValue: 100000000, status: "Completed" },
    { key: "pr-4", prId: "PR-2015", prDate: "15-Mar-2024", amount: 200000000, poId: "PO-4015", poValue: 120000000, grnValue: 80000000, invoiceValue: 80000000, status: "In Progress" },
  ],
  "Steel": [
    { key: "pr-3b", prId: "PR-2010", prDate: "10-Jan-2024", amount: 200000000, poId: "PO-4010", poValue: 180000000, grnValue: 120000000, invoiceValue: 100000000, status: "Completed" },
    { key: "pr-4b", prId: "PR-2015", prDate: "15-Mar-2024", amount: 200000000, poId: "PO-4015", poValue: 120000000, grnValue: 80000000, invoiceValue: 80000000, status: "In Progress" },
  ],
  "Sand & Aggregate": [
    { key: "pr-6", prId: "PR-2025", prDate: "15-Jan-2024", amount: 80000000, poId: "PO-4025", poValue: 70000000, grnValue: 50000000, invoiceValue: 45000000, status: "Completed" },
    { key: "pr-7", prId: "PR-2030", prDate: "10-Mar-2024", amount: 70000000, poId: "PO-4030", poValue: 50000000, grnValue: 40000000, invoiceValue: 40000000, status: "In Progress" },
  ],
  "Electrical Cables": [
    { key: "pr-5", prId: "PR-2020", prDate: "01-Feb-2024", amount: 90000000, poId: "PO-4020", poValue: 75000000, grnValue: 50000000, invoiceValue: 45000000, status: "Completed" },
  ],
  "Plumbing Materials": [
    { key: "pr-8", prId: "PR-2035", prDate: "20-Feb-2024", amount: 60000000, poId: "PO-4035", poValue: 50000000, grnValue: 35000000, invoiceValue: 30000000, status: "In Progress" },
  ],
};

// Drilldown for Expense Category → Expense Entries
const drilldownExpenseEntries: Record<string, any[]> = {
  "Diesel": [
    { key: "exp-1", expId: "EXP-101", date: "10-Jan-2024", description: "Diesel for DG Set", amount: 500000, approvedBy: "Site Manager", status: "Paid" },
    { key: "exp-2", expId: "EXP-115", date: "25-Feb-2024", description: "Diesel for Equipment", amount: 600000, approvedBy: "Site Manager", status: "Paid" },
    { key: "exp-3", expId: "EXP-130", date: "15-Mar-2024", description: "Diesel for Transport", amount: 400000, approvedBy: "Project Head", status: "Approved" },
  ],
  "Travel": [
    { key: "exp-4", expId: "EXP-201", date: "05-Jan-2024", description: "Site Visit - HQ", amount: 150000, approvedBy: "Admin", status: "Paid" },
    { key: "exp-5", expId: "EXP-220", date: "18-Mar-2024", description: "Material Inspection", amount: 150000, approvedBy: "QA Manager", status: "Approved" },
  ],
  "Site Office": [
    { key: "exp-6", expId: "EXP-301", date: "01-Jan-2024", description: "Office Rent", amount: 200000, approvedBy: "Admin", status: "Paid" },
    { key: "exp-7", expId: "EXP-302", date: "01-Feb-2024", description: "Office Rent", amount: 200000, approvedBy: "Admin", status: "Paid" },
    { key: "exp-8", expId: "EXP-303", date: "01-Mar-2024", description: "Office Supplies", amount: 100000, approvedBy: "Admin", status: "Approved" },
  ],
  "Safety Equipment": [
    { key: "exp-9", expId: "EXP-401", date: "15-Jan-2024", description: "Helmets & Vests", amount: 350000, approvedBy: "Safety Officer", status: "Paid" },
    { key: "exp-10", expId: "EXP-420", date: "10-Mar-2024", description: "Safety Nets", amount: 300000, approvedBy: "Safety Officer", status: "Approved" },
  ],
  "Miscellaneous": [
    { key: "exp-11", expId: "EXP-501", date: "20-Jan-2024", description: "Stationery & Printing", amount: 50000, approvedBy: "Admin", status: "Paid" },
    { key: "exp-12", expId: "EXP-510", date: "15-Mar-2024", description: "Water Supply Charges", amount: 80000, approvedBy: "Site Manager", status: "Paid" },
    { key: "exp-13", expId: "EXP-520", date: "01-Apr-2024", description: "Site Cleaning", amount: 70000, approvedBy: "Admin", status: "Approved" },
  ],
};

// Drilldown for Work Package → Work Order → Bills
const drilldownWorkPackageWOs: Record<string, any[]> = {
  "RCC Works": [
    { key: "wpwo-1", woId: "WO-5001", contractor: "ABC", woValue: 250000000, raBills: 180000000, paid: 120000000, status: "In Progress" },
    { key: "wpwo-2", woId: "WO-5002", contractor: "ABC", woValue: 200000000, raBills: 120000000, paid: 80000000, status: "In Progress" },
  ],
  "RCC Slab": [
    { key: "wpwo-1b", woId: "WO-5001", contractor: "ABC", woValue: 250000000, raBills: 180000000, paid: 120000000, status: "In Progress" },
    { key: "wpwo-2b", woId: "WO-5002", contractor: "ABC", woValue: 200000000, raBills: 120000000, paid: 80000000, status: "In Progress" },
  ],
  "Foundation": [
    { key: "wpwo-3", woId: "WO-5010", contractor: "XYZ", woValue: 300000000, raBills: 250000000, paid: 200000000, status: "Completed" },
    { key: "wpwo-4", woId: "WO-5011", contractor: "XYZ", woValue: 200000000, raBills: 150000000, paid: 100000000, status: "In Progress" },
  ],
  "MEP Works": [
    { key: "wpwo-5", woId: "WO-5020", contractor: "DEF", woValue: 280000000, raBills: 200000000, paid: 150000000, status: "In Progress" },
  ],
  "Finishing Works": [
    { key: "wpwo-6", woId: "WO-5030", contractor: "GHI", woValue: 220000000, raBills: 150000000, paid: 100000000, status: "In Progress" },
  ],
  "External Development": [
    { key: "wpwo-7", woId: "WO-5040", contractor: "JKL", woValue: 180000000, raBills: 100000000, paid: 70000000, status: "In Progress" },
  ],
};

// Drilldown for Month → Transactions
const drilldownMonthTransactions: Record<string, any[]> = {
  "January": [
    { key: "mt-1", txnId: "PO-4001", type: "Purchase Order", description: "Cement Supply", amount: 90000000, date: "10-Jan-2024" },
    { key: "mt-2", txnId: "WO-5010", type: "Work Order", description: "Foundation Work", amount: 150000000, date: "15-Jan-2024" },
    { key: "mt-3", txnId: "EXP-101", type: "Expense", description: "Diesel for DG Set", amount: 500000, date: "10-Jan-2024" },
  ],
  "February": [
    { key: "mt-4", txnId: "PO-4005", type: "Purchase Order", description: "Cement Supply - Phase 2", amount: 60000000, date: "20-Feb-2024" },
    { key: "mt-5", txnId: "WO-5001", type: "Work Order", description: "RCC Slab - Block A", amount: 250000000, date: "05-Feb-2024" },
    { key: "mt-6", txnId: "RA-101", type: "RA Bill", description: "RCC Works Progress", amount: 200000000, date: "15-Feb-2024" },
  ],
  "March": [
    { key: "mt-7", txnId: "PO-4010", type: "Purchase Order", description: "Steel TMT Supply", amount: 180000000, date: "10-Mar-2024" },
    { key: "mt-8", txnId: "RA-102", type: "RA Bill", description: "RCC Works Progress-2", amount: 200000000, date: "20-Mar-2024" },
    { key: "mt-9", txnId: "EXP-130", type: "Expense", description: "Diesel for Transport", amount: 400000, date: "15-Mar-2024" },
  ],
  "April": [
    { key: "mt-10", txnId: "PO-4015", type: "Purchase Order", description: "Steel TMT Phase 2", amount: 120000000, date: "05-Apr-2024" },
    { key: "mt-11", txnId: "WO-5020", type: "Work Order", description: "MEP Works", amount: 280000000, date: "10-Apr-2024" },
    { key: "mt-12", txnId: "RA-301", type: "RA Bill", description: "Electrical Progress", amount: 180000000, date: "05-Apr-2024" },
  ],
  "May": [
    { key: "mt-13", txnId: "WO-5002", type: "Work Order", description: "RCC Slab - Block B", amount: 200000000, date: "01-May-2024" },
    { key: "mt-14", txnId: "RA-201", type: "RA Bill", description: "MEP Progress", amount: 250000000, date: "25-May-2024" },
    { key: "mt-15", txnId: "RA-302", type: "RA Bill", description: "Electrical Progress-2", amount: 170000000, date: "15-May-2024" },
  ],
  "June": [
    { key: "mt-16", txnId: "PO-4020", type: "Purchase Order", description: "Electrical Cables", amount: 75000000, date: "10-Jun-2024" },
    { key: "mt-17", txnId: "WO-5011", type: "Work Order", description: "Foundation Phase 2", amount: 200000000, date: "15-Jun-2024" },
  ],
};

// Drilldown for Bill → Payment Documents (Report 3 & 6)
const drilldownBillDocuments: Record<string, any[]> = {
  "RA-101": [
    { key: "bd-1", docId: "PAY-6001", type: "Payment Voucher", amount: 180000000, date: "20-Feb-2024", status: "Paid", refNo: "NEFT-90001" },
    { key: "bd-2", docId: "DED-6001", type: "Deduction Note", amount: 20000000, date: "20-Feb-2024", status: "Applied", refNo: "TDS-2%" },
  ],
  "RA-102": [
    { key: "bd-3", docId: "PAY-6005", type: "Payment Voucher", amount: 200000000, date: "25-Mar-2024", status: "Pending", refNo: "NEFT-90005" },
  ],
  "RA-103": [
    { key: "bd-4", docId: "PAY-6010", type: "Payment Voucher", amount: 270000000, date: "15-Jan-2024", status: "Paid", refNo: "NEFT-90010" },
    { key: "bd-5", docId: "DED-6010", type: "Deduction Note", amount: 30000000, date: "15-Jan-2024", status: "Applied", refNo: "Retention-10%" },
  ],
  "RA-201": [
    { key: "bd-6", docId: "PAY-6015", type: "Payment Voucher", amount: 225000000, date: "30-Mar-2024", status: "Pending", refNo: "NEFT-90015" },
  ],
  "RA-202": [
    { key: "bd-7", docId: "PAY-6018", type: "Payment Voucher", amount: 135000000, date: "15-Apr-2024", status: "Paid", refNo: "NEFT-90018" },
    { key: "bd-8", docId: "DED-6018", type: "Deduction Note", amount: 15000000, date: "15-Apr-2024", status: "Applied", refNo: "TDS-2%" },
  ],
  "RA-301": [
    { key: "bd-9", docId: "PAY-6020", type: "Payment Voucher", amount: 162000000, date: "10-Apr-2024", status: "Paid", refNo: "NEFT-90020" },
    { key: "bd-10", docId: "DED-6020", type: "Deduction Note", amount: 18000000, date: "10-Apr-2024", status: "Applied", refNo: "Retention-10%" },
  ],
  "RA-302": [
    { key: "bd-11", docId: "PAY-6025", type: "Payment Voucher", amount: 170000000, date: "20-May-2024", status: "Pending", refNo: "NEFT-90025" },
  ],
  "RA-401": [
    { key: "bd-12", docId: "PAY-6030", type: "Payment Voucher", amount: 72000000, date: "25-Apr-2024", status: "Paid", refNo: "NEFT-90030" },
    { key: "bd-13", docId: "DED-6030", type: "Deduction Note", amount: 8000000, date: "25-Apr-2024", status: "Applied", refNo: "TDS-2%" },
  ],
  "RA-402": [
    { key: "bd-14", docId: "PAY-6035", type: "Payment Voucher", amount: 70000000, date: "30-May-2024", status: "Pending", refNo: "NEFT-90035" },
  ],
  "RA-501": [
    { key: "bd-15", docId: "PAY-6040", type: "Payment Voucher", amount: 80000000, date: "05-Jun-2024", status: "Pending", refNo: "NEFT-90040" },
  ],
  "RA-601": [
    { key: "bd-16", docId: "PAY-6045", type: "Payment Voucher", amount: 90000000, date: "15-Mar-2024", status: "Paid", refNo: "NEFT-90045" },
    { key: "bd-17", docId: "DED-6045", type: "Deduction Note", amount: 10000000, date: "15-Mar-2024", status: "Applied", refNo: "TDS-2%" },
  ],
  "RA-602": [
    { key: "bd-18", docId: "PAY-6048", type: "Payment Voucher", amount: 80000000, date: "20-Apr-2024", status: "Pending", refNo: "NEFT-90048" },
  ],
  "RA-603": [
    { key: "bd-19", docId: "PAY-6050", type: "Payment Voucher", amount: 120000000, date: "25-Apr-2024", status: "Pending", refNo: "NEFT-90050" },
  ],
  "RA-701": [
    { key: "bd-20", docId: "PAY-6055", type: "Payment Voucher", amount: 135000000, date: "20-Feb-2024", status: "Paid", refNo: "NEFT-90055" },
    { key: "bd-21", docId: "DED-6055", type: "Deduction Note", amount: 15000000, date: "20-Feb-2024", status: "Applied", refNo: "Retention-10%" },
  ],
  "RA-702": [
    { key: "bd-22", docId: "PAY-6058", type: "Payment Voucher", amount: 100000000, date: "25-Mar-2024", status: "Paid", refNo: "NEFT-90058" },
  ],
  "RA-703": [
    { key: "bd-23", docId: "PAY-6060", type: "Payment Voucher", amount: 150000000, date: "15-Apr-2024", status: "Pending", refNo: "NEFT-90060" },
  ],
  "RA-801": [
    { key: "bd-24", docId: "PAY-6065", type: "Payment Voucher", amount: 200000000, date: "10-May-2024", status: "Pending", refNo: "NEFT-90065" },
  ],
  "RA-901": [
    { key: "bd-25", docId: "PAY-6070", type: "Payment Voucher", amount: 150000000, date: "20-May-2024", status: "Pending", refNo: "NEFT-90070" },
  ],
  "RA-1001": [
    { key: "bd-26", docId: "PAY-6075", type: "Payment Voucher", amount: 100000000, date: "05-Jun-2024", status: "Pending", refNo: "NEFT-90075" },
  ],
};

// Drilldown for PR → PO → GRN → Invoice Documents (Report 4)
const drilldownPRDocuments: Record<string, any[]> = {
  "PR-2001": [
    { key: "prd-1", docId: "PO-4001", type: "Purchase Order", vendor: "ACC Cement", amount: 90000000, date: "10-Jan-2024", status: "Completed" },
    { key: "prd-2", docId: "GRN-7001", type: "Goods Receipt", vendor: "ACC Cement", amount: 60000000, date: "25-Jan-2024", status: "Received" },
    { key: "prd-3", docId: "INV-8001", type: "Vendor Invoice", vendor: "ACC Cement", amount: 50000000, date: "05-Feb-2024", status: "Paid" },
  ],
  "PR-2005": [
    { key: "prd-4", docId: "PO-4005", type: "Purchase Order", vendor: "UltraTech Cement", amount: 60000000, date: "25-Feb-2024", status: "In Progress" },
    { key: "prd-5", docId: "GRN-7005", type: "Goods Receipt", vendor: "UltraTech Cement", amount: 40000000, date: "15-Mar-2024", status: "Received" },
    { key: "prd-6", docId: "INV-8005", type: "Vendor Invoice", vendor: "UltraTech Cement", amount: 30000000, date: "25-Mar-2024", status: "Pending" },
  ],
  "PR-2010": [
    { key: "prd-7", docId: "PO-4010", type: "Purchase Order", vendor: "TATA Steel", amount: 180000000, date: "15-Jan-2024", status: "Completed" },
    { key: "prd-8", docId: "GRN-7010", type: "Goods Receipt", vendor: "TATA Steel", amount: 120000000, date: "10-Feb-2024", status: "Received" },
    { key: "prd-9", docId: "INV-8010", type: "Vendor Invoice", vendor: "TATA Steel", amount: 100000000, date: "25-Feb-2024", status: "Paid" },
  ],
  "PR-2015": [
    { key: "prd-10", docId: "PO-4015", type: "Purchase Order", vendor: "JSW Steel", amount: 120000000, date: "20-Mar-2024", status: "In Progress" },
    { key: "prd-11", docId: "GRN-7015", type: "Goods Receipt", vendor: "JSW Steel", amount: 80000000, date: "10-Apr-2024", status: "Received" },
    { key: "prd-12", docId: "INV-8015", type: "Vendor Invoice", vendor: "JSW Steel", amount: 80000000, date: "25-Apr-2024", status: "Pending" },
  ],
  "PR-2020": [
    { key: "prd-13", docId: "PO-4020", type: "Purchase Order", vendor: "Havells Cables", amount: 75000000, date: "10-Feb-2024", status: "Completed" },
    { key: "prd-14", docId: "GRN-7020", type: "Goods Receipt", vendor: "Havells Cables", amount: 50000000, date: "01-Mar-2024", status: "Received" },
    { key: "prd-15", docId: "INV-8020", type: "Vendor Invoice", vendor: "Havells Cables", amount: 45000000, date: "15-Mar-2024", status: "Paid" },
  ],
  "PR-2025": [
    { key: "prd-16", docId: "PO-4025", type: "Purchase Order", vendor: "Local Supplier", amount: 70000000, date: "20-Jan-2024", status: "Completed" },
    { key: "prd-17", docId: "GRN-7025", type: "Goods Receipt", vendor: "Local Supplier", amount: 50000000, date: "05-Feb-2024", status: "Received" },
    { key: "prd-18", docId: "INV-8025", type: "Vendor Invoice", vendor: "Local Supplier", amount: 45000000, date: "20-Feb-2024", status: "Paid" },
  ],
  "PR-2030": [
    { key: "prd-19", docId: "PO-4030", type: "Purchase Order", vendor: "Sand Corp", amount: 50000000, date: "15-Mar-2024", status: "In Progress" },
    { key: "prd-20", docId: "GRN-7030", type: "Goods Receipt", vendor: "Sand Corp", amount: 40000000, date: "05-Apr-2024", status: "Received" },
    { key: "prd-21", docId: "INV-8030", type: "Vendor Invoice", vendor: "Sand Corp", amount: 40000000, date: "20-Apr-2024", status: "Pending" },
  ],
  "PR-2035": [
    { key: "prd-22", docId: "PO-4035", type: "Purchase Order", vendor: "Astral Pipes", amount: 50000000, date: "01-Mar-2024", status: "In Progress" },
    { key: "prd-23", docId: "GRN-7035", type: "Goods Receipt", vendor: "Astral Pipes", amount: 35000000, date: "20-Mar-2024", status: "Received" },
    { key: "prd-24", docId: "INV-8035", type: "Vendor Invoice", vendor: "Astral Pipes", amount: 30000000, date: "05-Apr-2024", status: "Pending" },
  ],
};

// Drilldown for Expense Entry → Voucher/Documents (Report 5)
const drilldownExpenseDocuments: Record<string, any[]> = {
  "EXP-101": [
    { key: "ed-1", docId: "VOUCHER-7001", type: "Expense Voucher", amount: 500000, date: "10-Jan-2024", approvedBy: "Site Manager", paymentMode: "Petty Cash", status: "Paid" },
  ],
  "EXP-115": [
    { key: "ed-2", docId: "VOUCHER-7010", type: "Expense Voucher", amount: 600000, date: "25-Feb-2024", approvedBy: "Site Manager", paymentMode: "Bank Transfer", status: "Paid" },
  ],
  "EXP-130": [
    { key: "ed-3", docId: "VOUCHER-7020", type: "Expense Voucher", amount: 400000, date: "15-Mar-2024", approvedBy: "Project Head", paymentMode: "Petty Cash", status: "Approved" },
  ],
  "EXP-201": [
    { key: "ed-4", docId: "VOUCHER-7030", type: "Travel Voucher", amount: 150000, date: "05-Jan-2024", approvedBy: "Admin", paymentMode: "Reimbursement", status: "Paid" },
  ],
  "EXP-220": [
    { key: "ed-5", docId: "VOUCHER-7035", type: "Travel Voucher", amount: 150000, date: "18-Mar-2024", approvedBy: "QA Manager", paymentMode: "Reimbursement", status: "Approved" },
  ],
  "EXP-301": [
    { key: "ed-6", docId: "VOUCHER-7040", type: "Rent Receipt", amount: 200000, date: "01-Jan-2024", approvedBy: "Admin", paymentMode: "Bank Transfer", status: "Paid" },
  ],
  "EXP-302": [
    { key: "ed-7", docId: "VOUCHER-7045", type: "Rent Receipt", amount: 200000, date: "01-Feb-2024", approvedBy: "Admin", paymentMode: "Bank Transfer", status: "Paid" },
  ],
  "EXP-303": [
    { key: "ed-8", docId: "VOUCHER-7050", type: "Expense Voucher", amount: 100000, date: "01-Mar-2024", approvedBy: "Admin", paymentMode: "Petty Cash", status: "Approved" },
  ],
  "EXP-401": [
    { key: "ed-9", docId: "VOUCHER-7055", type: "Purchase Voucher", amount: 350000, date: "15-Jan-2024", approvedBy: "Safety Officer", paymentMode: "Bank Transfer", status: "Paid" },
  ],
  "EXP-420": [
    { key: "ed-10", docId: "VOUCHER-7060", type: "Purchase Voucher", amount: 300000, date: "10-Mar-2024", approvedBy: "Safety Officer", paymentMode: "Bank Transfer", status: "Approved" },
  ],
  "EXP-501": [
    { key: "ed-11", docId: "VOUCHER-7065", type: "Petty Cash Voucher", amount: 50000, date: "20-Jan-2024", approvedBy: "Admin", paymentMode: "Petty Cash", status: "Paid" },
  ],
  "EXP-510": [
    { key: "ed-12", docId: "VOUCHER-7070", type: "Expense Voucher", amount: 80000, date: "15-Mar-2024", approvedBy: "Site Manager", paymentMode: "Petty Cash", status: "Paid" },
  ],
  "EXP-520": [
    { key: "ed-13", docId: "VOUCHER-7075", type: "Expense Voucher", amount: 70000, date: "01-Apr-2024", approvedBy: "Admin", paymentMode: "Petty Cash", status: "Approved" },
  ],
};

// Drilldown for Month Transactions → Documents (Report 7)
const drilldownMonthTxnDocuments: Record<string, any[]> = {
  "PO-4001": [
    { key: "mtd-1", docId: "GRN-7001", type: "Goods Receipt", amount: 60000000, date: "25-Jan-2024", status: "Received" },
    { key: "mtd-2", docId: "INV-8001", type: "Vendor Invoice", amount: 50000000, date: "05-Feb-2024", status: "Paid" },
  ],
  "WO-5010": [
    { key: "mtd-3", docId: "RA-701", type: "RA Bill", amount: 150000000, date: "15-Feb-2024", status: "Paid" },
  ],
  "EXP-101": [
    { key: "mtd-4", docId: "VOUCHER-7001", type: "Expense Voucher", amount: 500000, date: "10-Jan-2024", status: "Paid" },
  ],
  "PO-4005": [
    { key: "mtd-5", docId: "GRN-7005", type: "Goods Receipt", amount: 40000000, date: "15-Mar-2024", status: "Received" },
    { key: "mtd-6", docId: "INV-8005", type: "Vendor Invoice", amount: 30000000, date: "25-Mar-2024", status: "Pending" },
  ],
  "WO-5001": [
    { key: "mtd-7", docId: "RA-601", type: "RA Bill", amount: 100000000, date: "10-Mar-2024", status: "Paid" },
    { key: "mtd-8", docId: "RA-602", type: "RA Bill", amount: 80000000, date: "15-Apr-2024", status: "Approved" },
  ],
  "RA-101": [
    { key: "mtd-9", docId: "PAY-6001", type: "Payment Voucher", amount: 180000000, date: "20-Feb-2024", status: "Paid" },
  ],
  "PO-4010": [
    { key: "mtd-10", docId: "GRN-7010", type: "Goods Receipt", amount: 120000000, date: "10-Feb-2024", status: "Received" },
    { key: "mtd-11", docId: "INV-8010", type: "Vendor Invoice", amount: 100000000, date: "25-Feb-2024", status: "Paid" },
  ],
  "RA-102": [
    { key: "mtd-12", docId: "PAY-6005", type: "Payment Voucher", amount: 200000000, date: "25-Mar-2024", status: "Pending" },
  ],
  "EXP-130": [
    { key: "mtd-13", docId: "VOUCHER-7020", type: "Expense Voucher", amount: 400000, date: "15-Mar-2024", status: "Approved" },
  ],
  "PO-4015": [
    { key: "mtd-14", docId: "GRN-7015", type: "Goods Receipt", amount: 80000000, date: "10-Apr-2024", status: "Received" },
    { key: "mtd-15", docId: "INV-8015", type: "Vendor Invoice", amount: 80000000, date: "25-Apr-2024", status: "Pending" },
  ],
  "WO-5020": [
    { key: "mtd-16", docId: "RA-801", type: "RA Bill", amount: 200000000, date: "01-May-2024", status: "Submitted" },
  ],
  "RA-301": [
    { key: "mtd-17", docId: "PAY-6020", type: "Payment Voucher", amount: 162000000, date: "10-Apr-2024", status: "Paid" },
  ],
  "WO-5002": [
    { key: "mtd-18", docId: "RA-603", type: "RA Bill", amount: 120000000, date: "20-Apr-2024", status: "Submitted" },
  ],
  "RA-201": [
    { key: "mtd-19", docId: "PAY-6015", type: "Payment Voucher", amount: 225000000, date: "30-Mar-2024", status: "Pending" },
  ],
  "RA-302": [
    { key: "mtd-20", docId: "PAY-6025", type: "Payment Voucher", amount: 170000000, date: "20-May-2024", status: "Pending" },
  ],
  "PO-4020": [
    { key: "mtd-21", docId: "GRN-7020", type: "Goods Receipt", amount: 50000000, date: "01-Mar-2024", status: "Received" },
    { key: "mtd-22", docId: "INV-8020", type: "Vendor Invoice", amount: 45000000, date: "15-Mar-2024", status: "Paid" },
  ],
  "WO-5011": [
    { key: "mtd-23", docId: "RA-703", type: "RA Bill", amount: 150000000, date: "10-Apr-2024", status: "Approved" },
  ],
};

// Helper to format Cr/L
const formatCr = (v: number) => {
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return formatCurrency(v, "INR");
};

// Accordion Table: only one row expanded at a time per table
const BaseTable = Table;
const AccordionTable: FC<any> = ({ expandable, ...rest }) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  if (!expandable) return <BaseTable {...rest} />;
  return (
    <BaseTable
      {...rest}
      expandable={{
        ...expandable,
        expandedRowKeys: expandedKeys,
        onExpand: (expanded: boolean, record: any) => {
          setExpandedKeys(expanded ? [record.key] : []);
        },
      }}
    />
  );
};

// ===================== COMPONENT =====================

const BudgetAnalysisReport: FC = () => {
  const { budgetId } = useParams<{ budgetId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [activeReportTab, setActiveReportTab] = useState("report1");

  // Filter states
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [selectedContractor, setSelectedContractor] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedBudgetLine, setSelectedBudgetLine] = useState<string>("all");
  const [selectedTransactionType, setSelectedTransactionType] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<any>(null);

  // Filter dropdown options (from API)
  const [projectDropdownOptions, setProjectDropdownOptions] = useState<{ value: string; label: string }[]>([]);
  const [contractorDropdownOptions, setContractorDropdownOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryDropdownOptions, setCategoryDropdownOptions] = useState<{ value: string; label: string }[]>([]);
  const [budgetLineDropdownOptions, setBudgetLineDropdownOptions] = useState<{ value: string; label: string }[]>([]);

  // Dashboard API state
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [consumptionSourceApiData, setConsumptionSourceApiData] = useState<any>(null);
  const [healthData, setHealthData] = useState<any[]>([]);
  const [burnData, setBurnData] = useState<any[]>([]);
  const [distributionByCategoryData, setDistributionByCategoryData] = useState<any[]>([]);
  const [lineItemData, setLineItemData] = useState<any[]>([]);
  const [poData, setPoData] = useState<any[]>([]);
  const [woData, setWoData] = useState<any[]>([]);
  const [projectTaskData, setProjectTaskData] = useState<any[]>([]);
  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [contractorData, setContractorData] = useState<any[]>([]);
  const [varianceApiData, setVarianceApiData] = useState<any>(null);
  const [forecastApiData, setForecastApiData] = useState<any>(null);
  const [alertsData, setAlertsData] = useState<any[]>([]);

  // Report tab API state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [reportPagination, setReportPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  // Filter value → label mappings
  const categoryMap: Record<string, string> = { civil: "Civil Works", mep: "MEP", finishing: "Finishing", external: "External Works", landscaping: "Landscaping" };
  const contractorMap: Record<string, string> = { abc: "ABC Constructions", xyz: "XYZ Infra", def: "DEF Engineering", pqr: "PQR Builders" };
  const budgetLineMap: Record<string, string> = { rcc: "RCC Slab", foundation: "Foundation", cement: "Cement", steel: "Steel TMT", electrical: "Electrical Cables" };

  // Fetch filter dropdowns
  useEffect(() => {
    if (!budgetId) return;
    const headerParams = { headerIds: budgetId };
    (async () => {
      try {
        const [siteRes, contractorRes, categoryRes, lineRes] = await Promise.allSettled([
          budgetApis.budgetDashboardSiteDropdown(),
          budgetApis.budgetDashboardContractorDropdown(headerParams),
          budgetApis.budgetDashboardCategoryDropdown(headerParams),
          budgetApis.budgetLinesDropdown({ headerId: budgetId }),
        ]);

        const pickList = (r: any) => {
          if (r.status !== "fulfilled") return [];
          const v = r.value;
          return v?.response?.data || v?.data || v?.response || [];
        };

        // Project dropdown — one option per budget, identified by headerId
        const budgets: any[] = pickList(siteRes);
        const projectOpts = budgets.map((b: any) => ({
          value: String(b.headerId),
          label: `${b.budgetName ?? ""}${b.siteName ? ` (${b.siteName})` : ""}`,
        }));
        setProjectDropdownOptions(projectOpts);
        // Preselect by matching URL param (headerId) against response headerId
        const matched = budgets.find(
          (b: any) => String(b.headerId ?? "") === String(budgetId)
        );
        if (matched) setSelectedProject(String(matched.headerId));

        // Contractors
        const contractors: any[] = pickList(contractorRes);
        setContractorDropdownOptions(
          contractors.map((c: any) => ({
            value: String(c.contractorId ?? c.id ?? c.value),
            label: String(c.contractorName ?? c.name ?? c.label ?? ""),
          }))
        );

        // Categories
        const categories: any[] = pickList(categoryRes);
        setCategoryDropdownOptions(
          categories.map((c: any) => ({
            value: String(c.budgetCategoryId ?? c.categoryId ?? c.id ?? c.value),
            label: String(c.categoryName ?? c.budgetCategoryName ?? c.name ?? c.label ?? ""),
          }))
        );

        // Budget Lines
        const lines: any[] = pickList(lineRes);
        setBudgetLineDropdownOptions(
          lines.map((l: any) => ({
            value: String(l.lineId ?? l.budgetLineId ?? l.id ?? l.value),
            label: String(l.lineName ?? l.budgetLineName ?? l.name ?? l.label ?? ""),
          }))
        );
      } catch (err) {
        console.error("Error loading filter dropdowns", err);
      }
    })();
  }, [budgetId]);

  // Helper: filter by project (single project) or aggregate all projects by groupKey
  const filterByProject = <T extends Record<string, any>>(data: T[], groupKey?: string): T[] => {
    if (selectedProject !== "all") return data.filter((d) => d.project === selectedProject);
    if (!groupKey) return data.filter((d) => d.project === "proj1");
    // Aggregate: group by groupKey, sum numeric fields
    const map: Record<string, T> = {};
    data.forEach((d) => {
      const k = d[groupKey];
      if (!map[k]) {
        map[k] = { ...d, key: d.key };
      } else {
        Object.keys(d).forEach((field) => {
          if (typeof d[field] === "number" && field !== "key") {
            (map[k] as any)[field] = ((map[k] as any)[field] || 0) + d[field];
          }
        });
      }
    });
    return Object.values(map);
  };

  // Active budget summary based on selected project
  const activeBudgetSummary = useMemo(() => {
    if (selectedProject === "all" || !budgetSummaryByProject[selectedProject]) return budgetSummaryByProject.proj1;
    return budgetSummaryByProject[selectedProject];
  }, [selectedProject]);

  // Active KPI cards based on selected project
  const activeKpiCards = useMemo(() => {
    if (selectedProject === "all") {
      return kpiCardsByProject.proj1.map((card: any, idx: number) => {
        const total = Object.keys(kpiCardsByProject).reduce((sum, proj) => sum + kpiCardsByProject[proj][idx].value, 0);
        return { ...card, value: total };
      });
    }
    return kpiCardsByProject[selectedProject] || kpiCardsByProject.proj1;
  }, [selectedProject]);

  // Active budget burn data
  const activeBudgetBurnData = useMemo(() => {
    if (selectedProject === "all") {
      const months = budgetBurnByProject.proj1.months;
      const burn = months.map((_: string, i: number) =>
        Object.keys(budgetBurnByProject).reduce((sum: number, proj: string) => sum + budgetBurnByProject[proj].burn[i], 0)
      );
      const cumulative = months.map((_: string, i: number) =>
        Object.keys(budgetBurnByProject).reduce((sum: number, proj: string) => sum + budgetBurnByProject[proj].cumulative[i], 0)
      );
      return { months, burn, cumulative };
    }
    return budgetBurnByProject[selectedProject] || budgetBurnByProject.proj1;
  }, [selectedProject]);

  // Filtered data
  const filteredCategoryData = useMemo(() => {
    let data = filterByProject(categoryDistributionData, "category");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.category === label);
    }
    return data;
  }, [selectedCategory, selectedProject]);

  const filteredBudgetHealthData = useMemo(() => {
    let data = filterByProject(budgetHealthData, "category");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.category === label);
    }
    return data;
  }, [selectedCategory, selectedProject]);

  const filteredBudgetLineData = useMemo(() => {
    let data = filterByProject(budgetLineAnalyticsData, "budgetLine");
    if (selectedBudgetLine !== "all") {
      const label = budgetLineMap[selectedBudgetLine];
      data = data.filter((d) => d.budgetLine === label);
    }
    if (selectedContractor !== "all") {
      const label = contractorMap[selectedContractor];
      data = data.filter((d) => d.contractor === label);
    }
    return data;
  }, [selectedBudgetLine, selectedContractor, selectedProject]);

  const filteredBudgetVsPOData = useMemo(() => {
    let data = filterByProject(budgetVsPOData, "budgetLine");
    if (selectedBudgetLine !== "all") {
      const label = budgetLineMap[selectedBudgetLine];
      data = data.filter((d) => d.budgetLine === label);
    }
    return data;
  }, [selectedBudgetLine, selectedProject]);

  const filteredBudgetVsWOData = useMemo(() => {
    let data = filterByProject(budgetVsWOData, "workPackage");
    if (selectedContractor !== "all") {
      const label = contractorMap[selectedContractor];
      data = data.filter((d) => d.contractor === label);
    }
    return data;
  }, [selectedContractor, selectedProject]);

  const filteredBudgetVsTasksData = useMemo(() => {
    return filterByProject(budgetVsTasksData, "task");
  }, [selectedProject]);

  const filteredExpenseBudgetData = useMemo(() => {
    return filterByProject(expenseBudgetData, "expenseType");
  }, [selectedProject]);

  const filteredContractorCostData = useMemo(() => {
    let data = filterByProject(contractorCostData, "contractor");
    if (selectedContractor !== "all") {
      const label = contractorMap[selectedContractor];
      data = data.filter((d) => d.contractor === label);
    }
    return data;
  }, [selectedContractor, selectedProject]);

  const filteredConsumptionSourceData = useMemo(() => {
    return filterByProject(consumptionSourceData, "name");
  }, [selectedProject]);

  const filteredBudgetAlerts = useMemo(() => {
    return filterByProject(budgetAlerts);
  }, [selectedProject]);

  const filteredVarianceData = useMemo(() => {
    let data = filterByProject(varianceAnalysisData, "category");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.category === label || d.category === label.replace(" Works", ""));
    }
    return data;
  }, [selectedCategory, selectedProject]);

  const filteredForecastData = useMemo(() => {
    let data = filterByProject(forecastData, "category");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.category === label || d.category === label.replace(" Works", ""));
    }
    return data;
  }, [selectedCategory, selectedProject]);

  // Report filtered data
  const filteredReport1Data = useMemo(() => {
    let data = filterByProject(projectBudgetVsActualReportData, "budgetCategory");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.budgetCategory === label);
    }
    return data;
  }, [selectedCategory, selectedProject]);

  const filteredReport3Data = useMemo(() => {
    let data = filterByProject(contractorConsumptionReportData, "contractor");
    if (selectedContractor !== "all") {
      const label = contractorMap[selectedContractor];
      data = data.filter((d) => d.contractor.includes(label.split(" ")[0]));
    }
    return data;
  }, [selectedContractor, selectedProject]);

  const filteredReport4Data = useMemo(() => {
    let data = filterByProject(budgetVsPOReportData, "budgetLine");
    if (selectedBudgetLine !== "all") {
      const label = budgetLineMap[selectedBudgetLine];
      data = data.filter((d) => d.budgetLine === label);
    }
    return data;
  }, [selectedBudgetLine, selectedProject]);

  const filteredReport5Data = useMemo(() => {
    return filterByProject(budgetVsExpensesReportData, "expenseCategory");
  }, [selectedProject]);

  const filteredReport6Data = useMemo(() => {
    return filterByProject(budgetVsWOReportData, "workPackage");
  }, [selectedProject]);

  const filteredUtilizationTrendData = useMemo(() => {
    return filterByProject(utilizationTrendReportData, "month");
  }, [selectedProject]);

  const filteredReport8Data = useMemo(() => {
    let data = filterByProject(forecastReportData, "category");
    if (selectedCategory !== "all") {
      const label = categoryMap[selectedCategory];
      data = data.filter((d) => d.category === label || d.category === label.replace(" Works", ""));
    }
    return data;
  }, [selectedCategory, selectedProject]);

  // Build common filter params applied to dashboard + report calls
  const buildFilterParams = () => {
    const headerIds = selectedProject && selectedProject !== "all" ? selectedProject : budgetId;
    const params: any = { headerIds };
    if (selectedContractor && selectedContractor !== "all") params.contractorIds = selectedContractor;
    if (selectedCategory && selectedCategory !== "all") params.budgetCategoryIds = selectedCategory;
    if (selectedBudgetLine && selectedBudgetLine !== "all") params.lineIds = selectedBudgetLine;
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].format ? dateRange[0].format("YYYY-MM-DD") : dateRange[0];
      params.endDate = dateRange[1].format ? dateRange[1].format("YYYY-MM-DD") : dateRange[1];
    }
    return params;
  };

  // Fetch report data from API
  const fetchReportData = async (reportTab: string, page = 1, pageSize = 10) => {
    setReportLoading(true);
    try {
      const params: any = { page, pageSize, ...buildFilterParams() };
      // Map txn type filter to modules param
      if (selectedTransactionType.length > 0) {
        const moduleMap: Record<string, string> = {
          po: "Procurement",
          invoice: "Procurement",
          wo: "Work Order",
          expense: "Expense",
        };
        const modules = Array.from(new Set(selectedTransactionType.map(t => moduleMap[t]).filter(Boolean)));
        if (modules.length > 0) params.modules = modules.join(",");
      }

      let apiCall;
      switch (reportTab) {
        case "report1": apiCall = budgetAnalysisDashboardApis.projectBudgetVsActualSummary; break;
        case "report2": apiCall = budgetAnalysisDashboardApis.budgetConsumptionByCategorySummary; break;
        case "report3": apiCall = budgetAnalysisDashboardApis.contractorCostSummary; break;
        case "report4": apiCall = budgetAnalysisDashboardApis.budgetVsPurchaseOrdersSummary; break;
        case "report5": apiCall = budgetAnalysisDashboardApis.budgetVsExpensesSummary; break;
        case "report6": apiCall = budgetAnalysisDashboardApis.budgetVsWorkOrdersSummary; break;
        case "report7": apiCall = budgetAnalysisDashboardApis.budgetUtilizationTrend; break;
        case "report8": apiCall = budgetAnalysisDashboardApis.budgetForecastSummary; break;
        default: return;
      }

      const res = await apiCall(params);
      const data = res?.response?.data || res?.data || [];
      const total = Number(res?.response?.totalCount || data.length);
      setReportData(data.map((item: any, idx: number) => ({ ...item, key: idx })));
      setReportPagination({ page, pageSize, total });
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  };

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    setDashboardLoading(true);
    try {
      const params: any = { ...buildFilterParams() };
      // Map txn type filter to modules param
      if (selectedTransactionType.length > 0) {
        const moduleMap: Record<string, string> = {
          po: "Procurement",
          invoice: "Procurement",
          wo: "Work Order",
          expense: "Expense",
        };
        const modules = Array.from(new Set(selectedTransactionType.map(t => moduleMap[t]).filter(Boolean)));
        if (modules.length > 0) params.modules = modules.join(",");
      }

      const results = await Promise.allSettled([
        budgetAnalysisDashboardApis.summaryReport(params),
        budgetAnalysisDashboardApis.budgetVsActualByCategory(params),
        budgetAnalysisDashboardApis.budgetConsumptionBySource(params),
        budgetAnalysisDashboardApis.budgetHealthIndicator(params),
        budgetAnalysisDashboardApis.timeBudgetBurn(params),
        budgetAnalysisDashboardApis.budgetDistributionByCategory(params),
        budgetAnalysisDashboardApis.budgetDistributionByLineItem(params),
        budgetAnalysisDashboardApis.budgetVsPurchaseOrders(params),
        budgetAnalysisDashboardApis.budgetVsWorkOrders(params),
        budgetAnalysisDashboardApis.budgetVsProjectTasks(params),
        budgetAnalysisDashboardApis.expenseBudgetAnalysis(params),
        budgetAnalysisDashboardApis.contractorCostPerformance(params),
        budgetAnalysisDashboardApis.budgetVarianceAnalysis(params),
        budgetAnalysisDashboardApis.forecastCostAtCompletion(params),
        budgetAnalysisDashboardApis.budgetControlAlerts(params),
      ]);

      const getData = (result: any) => {
        if (result.status === "fulfilled") {
          const res = result.value;
          return res?.response?.data || res?.data || [];
        }
        return [];
      };

      const addKeys = (arr: any[]) => Array.isArray(arr) ? arr.map((item: any, idx: number) => ({ ...item, key: idx })) : [];

      setSummaryData(getData(results[0])?.[0] || null);
      setCategoryData(addKeys(getData(results[1])));
      setConsumptionSourceApiData(getData(results[2])?.[0] || null);
      setHealthData(addKeys(getData(results[3])));
      setBurnData(addKeys(getData(results[4])));
      setDistributionByCategoryData(addKeys(getData(results[5])));
      setLineItemData(addKeys(getData(results[6])));
      setPoData(addKeys(getData(results[7])));
      setWoData(addKeys(getData(results[8])));
      setProjectTaskData(addKeys(getData(results[9])));
      setExpenseData(addKeys(getData(results[10])));
      setContractorData(addKeys(getData(results[11])));
      setVarianceApiData(getData(results[12])?.[0] || null);
      setForecastApiData(getData(results[13])?.[0] || null);
      setAlertsData(addKeys(getData(results[14])));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchDashboardData();
    }
  }, [activeTab, selectedTransactionType, selectedProject, selectedContractor, selectedCategory, selectedBudgetLine, dateRange]);

  // Chart refs
  const budgetVsActualChartRef = useRef<HTMLDivElement>(null);
  const consumptionSourceChartRef = useRef<HTMLDivElement>(null);
  const budgetBurnChartRef = useRef<HTMLDivElement>(null);
  const budgetDistributionChartRef = useRef<HTMLDivElement>(null);
  const utilizationTrendChartRef = useRef<HTMLDivElement>(null);

  const budgetVsActualChart = useRef<echarts.ECharts | null>(null);
  const consumptionSourceChart = useRef<echarts.ECharts | null>(null);
  const budgetBurnChart = useRef<echarts.ECharts | null>(null);
  const budgetDistributionChart = useRef<echarts.ECharts | null>(null);
  const utilizationTrendChart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (activeTab === "dashboard") {
      setTimeout(() => {
        initBudgetDistributionChart();
        initConsumptionSourceChart();
        initBudgetBurnChart();
      }, 100);
    }
    return () => {
      budgetVsActualChart.current?.dispose();
      consumptionSourceChart.current?.dispose();
      budgetBurnChart.current?.dispose();
      budgetDistributionChart.current?.dispose();
      budgetVsActualChart.current = null;
      consumptionSourceChart.current = null;
      budgetBurnChart.current = null;
      budgetDistributionChart.current = null;
    };
  }, [activeTab, selectedProject, selectedCategory, selectedContractor, selectedBudgetLine, categoryData, consumptionSourceApiData, burnData]);

  useEffect(() => {
    if (activeTab === "reports" && activeReportTab === "report7") {
      setTimeout(() => {
        initUtilizationTrendChart();
      }, 150);
    }
    return () => {
      utilizationTrendChart.current?.dispose();
      utilizationTrendChart.current = null;
    };
  }, [activeTab, activeReportTab, selectedProject]);

  // Fetch report data when report tab changes
  useEffect(() => {
    if (activeTab === "reports") {
      fetchReportData(activeReportTab);
    }
  }, [activeTab, activeReportTab, selectedTransactionType, selectedProject, selectedContractor, selectedCategory, selectedBudgetLine, dateRange]);

  useEffect(() => {
    const handleResize = () => {
      budgetVsActualChart.current?.resize();
      consumptionSourceChart.current?.resize();
      budgetBurnChart.current?.resize();
      budgetDistributionChart.current?.resize();
      utilizationTrendChart.current?.resize();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ===================== CHART INIT =====================

  const initBudgetDistributionChart = () => {
    if (!budgetDistributionChartRef.current) return;
    if (budgetDistributionChart.current) budgetDistributionChart.current.dispose();
    budgetDistributionChart.current = echarts.init(budgetDistributionChartRef.current);
    // Use API categoryData for chart
    const categories = categoryData.map((d: any) => d.budgetCategory || d.category || "");
    const chartData = categoryData.map((d: any) => ({
      budget: Number(d.totalBudget ?? d.budget ?? 0),
      committed: Number(d.totalCommitted ?? d.committed ?? 0),
      consumed: Number(d.totalConsumed ?? d.consumed ?? 0),
    }));
    budgetDistributionChart.current.setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p: any) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${formatCr(p.value)}`;
          });
          return html;
        },
      },
      legend: { data: ["Budget", "Committed", "Consumed"], bottom: 0 },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: categories, axisLabel: { rotate: 15 } },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Budget",
          type: "bar",
          data: chartData.map((d: any) => d.budget),
          itemStyle: { color: "#5470C6" },
          barWidth: "22%",
        },
        {
          name: "Committed",
          type: "bar",
          data: chartData.map((d: any) => d.committed),
          itemStyle: { color: "#91CC75" },
          barWidth: "22%",
        },
        {
          name: "Consumed",
          type: "bar",
          data: chartData.map((d: any) => d.consumed),
          itemStyle: { color: "#FAC858" },
          barWidth: "22%",
        },
      ],
    });
  };

  const initConsumptionSourceChart = () => {
    if (!consumptionSourceChartRef.current) return;
    if (consumptionSourceChart.current) consumptionSourceChart.current.dispose();
    consumptionSourceChart.current = echarts.init(consumptionSourceChartRef.current);
    // Build pie data: slice values are consumed amounts, hover shows pct from API
    const pieData: { name: string; value: number; pct: number }[] = (() => {
      if (consumptionSourceApiData) {
        const d = consumptionSourceApiData;
        const rows = [
          { name: "Procurement (PO + GRN)", value: Number(d.procurementConsumed ?? d.poInvoices ?? 0), pct: Number(d.procurementPct ?? 0) },
          { name: "Work Orders (Contractors)", value: Number(d.workOrderConsumed ?? d.woRaBills ?? 0), pct: Number(d.workOrderPct ?? 0) },
          { name: "Project Task Expenses", value: Number(d.projectTaskConsumed ?? 0), pct: Number(d.projectTaskPct ?? 0) },
          { name: "Petty Cash Expenses", value: Number(d.expenseConsumed ?? d.expenses ?? 0), pct: Number(d.expensePct ?? 0) },
        ];
        return rows;
      }
      const m: Record<string, number> = {};
      filteredConsumptionSourceData.forEach((d: any) => { m[d.name] = (m[d.name] || 0) + d.value; });
      return Object.entries(m).map(([name, value]) => ({ name, value, pct: 0 }));
    })();

    consumptionSourceChart.current.setOption({
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: (params: any) => {
          const apiPct = params.data?.pct;
          const pctText = apiPct != null && !isNaN(apiPct) ? `${apiPct}%` : `${params.percent}%`;
          return `${params.name}: ${formatCr(params.value)} (${pctText})`;
        },
      },
      legend: { orient: "horizontal", bottom: 0, left: "center", textStyle: { fontSize: 11 } },
      grid: { containLabel: true },
      series: [
        {
          name: "Consumption Source",
          type: "pie",
          radius: ["35%", "60%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
          label: { show: false },
          emphasis: { label: { show: false } },
          data: pieData,
          color: ["#5470C6", "#91CC75", "#FAC858", "#EE6666"],
        },
      ],
    });
  };

  const initBudgetBurnChart = () => {
    if (!budgetBurnChartRef.current) return;
    if (budgetBurnChart.current) budgetBurnChart.current.dispose();
    budgetBurnChart.current = echarts.init(budgetBurnChartRef.current);
    // Use API burnData if available, fallback to dummy
    let activeMonths: string[];
    let activeBurn: number[];
    let activeCum: number[];
    if (burnData.length > 0) {
      activeMonths = burnData.map((d: any) => d.monthLabel || d.monthName || d.month || "");
      activeBurn = burnData.map((d: any) => Number(d.totalMonthlyBurn ?? d.burn ?? d.monthlyBurn ?? 0));
      activeCum = burnData.map((d: any) => Number(d.cumulativeBurn ?? d.cumulative ?? 0));
    } else {
      activeMonths = activeBudgetBurnData.months.filter((_: string, i: number) => activeBudgetBurnData.burn[i] > 0);
      activeBurn = activeBudgetBurnData.burn.filter((v: number) => v > 0);
      activeCum = activeBudgetBurnData.cumulative.filter((v: number) => v > 0);
    }
    budgetBurnChart.current.setOption({
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p: any) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${formatCr(p.value)}`;
          });
          return html;
        },
      },
      legend: { data: ["Monthly Burn", "Cumulative"], bottom: 0 },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: activeMonths },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Monthly Burn",
          type: "bar",
          data: activeBurn,
          itemStyle: { color: "#5470C6" },
          barWidth: "40%",
        },
        {
          name: "Cumulative",
          type: "line",
          data: activeCum,
          lineStyle: { color: "#EE6666", width: 3 },
          itemStyle: { color: "#EE6666" },
          smooth: true,
        },
      ],
    });
  };

  const initUtilizationTrendChart = () => {
    if (!utilizationTrendChartRef.current) return;
    if (utilizationTrendChart.current) utilizationTrendChart.current.dispose();
    utilizationTrendChart.current = echarts.init(utilizationTrendChartRef.current);
    const source = activeReportTab === "report7" && reportData.length > 0 ? reportData : filteredUtilizationTrendData;
    const months = source.map((d: any) => {
      const name = d.monthName || d.fullMonthName || d.month || "";
      return typeof name === "string" ? name.substring(0, 3) : String(name);
    });
    const values = source.map((d: any) => Number(d.consumedValue ?? d.budgetConsumed ?? 0));
    utilizationTrendChart.current.setOption({
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => `<strong>${params[0].name}</strong><br/>Budget Consumed: ${formatCr(params[0].value)}`,
      },
      grid: { left: "3%", right: "4%", bottom: "10%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: months },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Budget Consumed",
          type: "line",
          data: values,
          lineStyle: { color: "#5470C6", width: 3 },
          itemStyle: { color: "#5470C6" },
          areaStyle: { color: "rgba(84, 112, 198, 0.15)" },
          smooth: true,
        },
      ],
    });
  };

  // ===================== FILTERS =====================

  const renderFilters = () => (
    <Card className="card-bordered mb-3">
      <div style={{ padding: "16px 20px" }}>
        <div className="d-flex align-items-center mb-2">
          <FilterOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          <strong style={{ fontSize: "14px" }}>Filters</strong>
        </div>
        <Row className="g-2">
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Project</label>
            <Select
              value={selectedProject}
              onChange={setSelectedProject}
              style={{ width: "100%" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Projects</Option>
              {projectDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Contractor</label>
            <Select
              value={selectedContractor}
              onChange={setSelectedContractor}
              style={{ width: "100%" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Contractors</Option>
              {contractorDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Budget Category</label>
            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: "100%" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Categories</Option>
              {categoryDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Date Range</label>
            <RangePicker
              size="small"
              style={{ width: "100%" }}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Budget Line</label>
            <Select
              value={selectedBudgetLine}
              onChange={setSelectedBudgetLine}
              style={{ width: "100%" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Budget Lines</Option>
              {budgetLineDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Txn Type</label>
            <Select
              value={selectedTransactionType}
              onChange={setSelectedTransactionType}
              style={{ width: "100%" }}
              size="small"
              mode="multiple"
              maxTagCount={1}
              placeholder="All Transactions"
              allowClear
            >
              {transactionTypeOptions.filter(o => o.value !== "all").map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>
    </Card>
  );

  // ===================== DASHBOARD RENDER =====================

  const getHealthIcon = (status: string) => {
    if (status === "healthy") return <span style={{ fontSize: "18px" }}>🟢</span>;
    if (status === "monitor") return <span style={{ fontSize: "18px" }}>🟠</span>;
    return <span style={{ fontSize: "18px" }}>🔴</span>;
  };

  // Helper to safely convert string numbers from API
  const n = (val: any) => Number(val || 0);

  const renderDashboard = () => {
    const kpiCards = summaryData ? [
      { title: "Total Budget", value: Number(summaryData.totalBudget || 0), icon: <DollarOutlined />, color: "#1890ff" },
      { title: "Total Committed", value: Number(summaryData.totalCommitted || 0), icon: <BarChartOutlined />, color: "#722ed1", description: "PO + Work Orders" },
      { title: "Total Consumed", value: Number(summaryData.totalConsumed || 0), icon: <PieChartOutlined />, color: "#faad14", description: "Invoices + Expenses + RA Bills" },
      { title: "Available Budget", value: Number(summaryData.availableBudget || 0), icon: <CheckCircleOutlined />, color: "#52c41a" },
      { title: "Forecast at Completion", value: Number(summaryData.forecastAtCompletion || 0), icon: <FundProjectionScreenOutlined />, color: "#13c2c2" },
      { title: "Budget Variance", value: Number(summaryData.budgetVariance || 0), icon: <ArrowUpOutlined />, color: "#52c41a", trend: Number(summaryData.budgetVariance || 0) >= 0 ? "saving" : "overrun" },
    ] : [];

    return (
    <Spin spinning={dashboardLoading}>
    <div>
      {/* Row 1: 6 KPI Cards */}
      <Row className="g-gs mb-3">
        {kpiCards.map((kpi: any, idx: number) => (
          <Col lg="2" md="4" sm="6" key={idx}>
            <Card
              className="card-bordered"
              style={{ borderLeft: `4px solid ${kpi.color}`, padding: "14px 16px", height: "120px", display: "flex", flexDirection: "column", justifyContent: "center" }}
            >
              <div>
                <div className="d-flex justify-content-between align-items-start">
                  <p className="mb-1" style={{ color: "#8094ae", fontSize: "12px", fontWeight: 500 }}>
                    {kpi.title}
                  </p>
                  <span style={{ fontSize: "22px", color: kpi.color, opacity: 0.7 }}>
                    {kpi.icon}
                  </span>
                </div>
                <h5 className="mb-0" style={{ fontSize: "16px" }}>
                  {formatCr(kpi.value)}
                </h5>
                {(kpi as any).description && (
                  <small style={{ color: "#8094ae", fontSize: "10px" }}>
                    {(kpi as any).description}
                  </small>
                )}
                {kpi.trend === "saving" && (
                  <small style={{ color: "#52c41a", fontSize: "11px" }}>
                    <ArrowUpOutlined /> Saving
                  </small>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Row 2: Budget vs Actual by Category (Bar Chart) + Budget Consumption by Source (Pie Chart) */}
      <Row className="g-gs mb-3">
        <Col lg="8">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Budget vs Actual vs Committed by Category</h6>
            <div ref={budgetDistributionChartRef} style={{ width: "100%", height: "350px" }} />
          </Card>
        </Col>
        <Col lg="4">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Budget Consumption by Source</h6>
            <div ref={consumptionSourceChartRef} style={{ width: "100%", height: "350px" }} />
          </Card>
        </Col>
      </Row>

      {/* Row 3: Budget Health Indicator + Time vs Budget Burn */}
      <Row className="g-gs mb-3">
        <Col lg="5">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Budget Health Indicator</h6>
            <p style={{ color: "#8094ae", fontSize: "11px", marginBottom: "8px" }}>
              🟢 &lt;70% Healthy &nbsp; 🟠 70-90% Monitor &nbsp; 🔴 &gt;90% Risk
            </p>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Category",
                  key: "category",
                  width: 150,
                  render: (_: any, r: any) => r.budgetCategory || r.category || "",
                },
                {
                  title: "Utilization %",
                  key: "utilization",
                  width: 180,
                  render: (_: any, r: any) => {
                    const pct = Math.round(n(r.totalUtilizationPct ?? r.utilization));
                    return (
                    <Progress
                      percent={pct}
                      size="small"
                      strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                    />
                    );
                  },
                },
                {
                  title: "Status",
                  key: "status",
                  width: 80,
                  render: (_: string, record: any) =>
                    getHealthIcon(String(record.trafficLight ?? record.status ?? "").toLowerCase()),
                },
              ]}
              dataSource={healthData}
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
            />
            </div>
          </Card>
        </Col>
        <Col lg="7">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Time vs Budget Burn</h6>
            <div ref={budgetBurnChartRef} style={{ width: "100%", height: "320px" }} />
          </Card>
        </Col>
      </Row>

      {/* Row 4: Budget Distribution by Category Table (with Drilldown) */}
      <Row className="g-gs mb-3">
        <Col lg="12">
          <Card className="card-bordered p-3">
            <h6 className="mb-2">Budget Distribution by Category</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Category",
                  key: "category",
                  width: 150,
                  render: (_: any, r: any) => r.budgetCategory || r.category || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)),
                },
                {
                  title: "Committed",
                  key: "committed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.committedValue ?? r.committed)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.consumedValue ?? r.consumed)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 130,
                  render: (_: any, r: any) => {
                    const val = n(r.availableBudget ?? r.available);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Utilization %",
                  key: "utilization",
                  width: 150,
                  render: (_: any, r: any) => {
                    const apiPct = r.totalUtilizationPct != null ? Number(r.totalUtilizationPct) : null;
                    let pct: number;
                    if (apiPct != null && !isNaN(apiPct)) {
                      pct = Math.round(apiPct);
                    } else {
                      const budget = n(r.budgetValue ?? r.budget);
                      pct = budget > 0 ? Math.round(((n(r.committedValue ?? r.committed) + n(r.consumedValue ?? r.consumed)) / budget) * 100) : 0;
                    }
                    return <Progress percent={pct} size="small" strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"} />;
                  },
                },
              ]}
              dataSource={distributionByCategoryData}
              pagination={false}
              size="small"
              scroll={{ x: 820 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 5: Budget vs Purchase Orders */}
      <Row className="g-gs mb-3">
        <Col lg="12">
          <Card className="card-bordered p-3">
            <h6 className="mb-2">Budget vs Purchase Orders</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Budget Line",
                  key: "budgetLine",
                  width: 180,
                  ellipsis: true,
                  render: (_: any, r: any) => r.budgetLineName || r.budgetLine || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)),
                },
                {
                  title: "PO Value",
                  key: "poValue",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.poValue)),
                },
                {
                  title: "GRN Value",
                  key: "grnValue",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.grnValue)),
                },
                {
                  title: "Invoice Value",
                  key: "invoiceValue",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.invoiceValue)),
                },
                {
                  title: "PO vs Budget %",
                  key: "poPct",
                  width: 160,
                  render: (_: any, r: any) => {
                    const apiPct = r.poVsBudgetPct != null ? Number(r.poVsBudgetPct) : null;
                    let pct: number;
                    if (apiPct != null && !isNaN(apiPct)) {
                      pct = Math.round(apiPct);
                    } else {
                      const budget = n(r.budgetValue ?? r.budget);
                      pct = budget > 0 ? Math.round((n(r.poValue) / budget) * 100) : 0;
                    }
                    return (
                      <Progress
                        percent={Math.min(pct, 100)}
                        format={() => `${pct}%`}
                        size="small"
                        strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                      />
                    );
                  },
                },
                {
                  title: "Status",
                  key: "trafficLight",
                  width: 120,
                  render: (_: any, r: any) => {
                    const s = r.trafficLight || "";
                    const color =
                      s === "Healthy" ? "green" :
                      s === "Monitor" ? "orange" :
                      s === "Risk" ? "red" :
                      s === "No Activity" ? "default" :
                      s ? "blue" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={poData}
              pagination={false}
              size="small"
              scroll={{ x: 960 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 7: Budget vs Work Orders */}
      <Row className="g-gs mb-3">
        <Col lg="12">
          <Card className="card-bordered p-3">
            <h6 className="mb-2">Budget vs Work Orders</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 160 },
                { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 160 },
                { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: any) => formatCr(n(v)) },
                { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: any) => formatCr(n(v)) },
                { title: "RA Bills", dataIndex: "raBills", key: "raBills", width: 120, render: (v: any) => formatCr(n(v)) },
                { title: "Balance", dataIndex: "balance", key: "balance", width: 120, render: (v: any) => <span style={{ color: "#52c41a" }}>{formatCr(n(v))}</span> },
              ]}
              dataSource={woData}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 8: Budget vs Project Tasks */}
      <Row className="g-gs mb-3">
        <Col lg="6">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Budget vs Project Tasks</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Task",
                  key: "task",
                  width: 200,
                  ellipsis: true,
                  render: (_: any, r: any) => r.taskName || r.task || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.taskBudgetAllocated ?? r.budget)),
                },
                {
                  title: "Spent",
                  key: "spent",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.taskSpent ?? r.spent)),
                },
                {
                  title: "Balance",
                  key: "balance",
                  width: 120,
                  render: (_: any, r: any) => {
                    const val = n(r.taskBalance ?? r.balance);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Status",
                  key: "taskStatus",
                  width: 110,
                  render: (_: any, r: any) => {
                    const s = r.taskStatus || "";
                    const color = s === "On Track" ? "green" : s === "Near Budget" ? "orange" : s ? "red" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={projectTaskData}
              pagination={false}
              size="small"
              scroll={{ x: 510 }}
            />
            </div>
          </Card>
        </Col>

        {/* Row 9: Expense Budget Analysis */}
        <Col lg="6">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Expense Budget Analysis</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Expense",
                  key: "expenseName",
                  width: 180,
                  ellipsis: true,
                  render: (_: any, r: any) => r.expenseName || r.expenceNumber || "",
                },
                {
                  title: "Budget Line",
                  key: "budgetLineName",
                  width: 160,
                  ellipsis: true,
                  render: (_: any, r: any) => r.budgetLineName || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.expenseLineBudget ?? r.budget)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.expenseConsumed ?? r.actual)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 120,
                  render: (_: any, r: any) => {
                    const val = n(r.expenseAvailable);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Util %",
                  key: "expenseUtilPct",
                  width: 130,
                  render: (_: any, r: any) => {
                    const pct = Math.round(n(r.expenseUtilPct));
                    return (
                      <Progress
                        percent={Math.min(pct, 100)}
                        format={() => `${pct}%`}
                        size="small"
                        strokeColor={pct >= 100 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                      />
                    );
                  },
                },
                {
                  title: "Status",
                  key: "expenseStatus",
                  width: 120,
                  render: (_: any, r: any) => {
                    const s = r.expenseStatus || "";
                    const color =
                      s === "On Track" ? "green" :
                      s === "Near Budget" ? "orange" :
                      s === "Over Budget" ? "red" :
                      s === "No Activity" ? "default" :
                      s ? "blue" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={expenseData}
              pagination={false}
              size="small"
              scroll={{ x: 930 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 10: Contractor Cost Performance */}
      <Row className="g-gs mb-3">
        <Col lg="12">
          <Card className="card-bordered p-3">
            <h6 className="mb-2">Contractor Cost Performance</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 180 },
                { title: "Budget", dataIndex: "budget", key: "budget", width: 130, render: (v: any) => formatCr(n(v)) },
                { title: "Actual", dataIndex: "actual", key: "actual", width: 130, render: (v: any) => formatCr(n(v)) },
                {
                  title: "Variance",
                  dataIndex: "variance",
                  key: "variance",
                  width: 150,
                  render: (v: any, r: any) => {
                    const val = n(v);
                    return (
                    <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                      {val >= 0 ? "+" : ""}{formatCr(val)} {r.varianceType}
                    </span>
                    );
                  },
                },
                {
                  title: "Performance",
                  key: "performance",
                  width: 130,
                  render: (_: any, r: any) => (
                    <Tag color={n(r.variance) >= 0 ? "green" : "red"}>
                      {n(r.variance) >= 0 ? "Under Budget" : "Over Budget"}
                    </Tag>
                  ),
                },
              ]}
              dataSource={contractorData}
              pagination={false}
              size="small"
              scroll={{ x: 720 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 11: Budget Variance Analysis */}
      <Row className="g-gs mb-3">
        <Col lg="6">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Budget Variance Analysis</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                },
                {
                  title: "Forecast",
                  key: "forecast",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion ?? r.forecast)),
                },
                {
                  title: "Variance",
                  key: "variance",
                  width: 150,
                  render: (_: any, r: any) => {
                    const val = n(r.Variance ?? r.variance);
                    const pct = r.variancePct != null ? Number(r.variancePct).toFixed(2) : null;
                    return (
                      <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                        {val >= 0 ? "+" : ""}{formatCr(val)}{pct != null ? ` (${pct}%)` : ""}
                      </span>
                    );
                  },
                },
                {
                  title: "Status",
                  key: "varianceStatus",
                  width: 110,
                  render: (_: any, r: any) => {
                    const s = r.varianceStatus || "";
                    const color = s === "Healthy" ? "green" : s === "Monitor" ? "orange" : s ? "red" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={varianceApiData ? [{ ...varianceApiData, key: 0 }] : []}
              pagination={false}
              size="small"
              scroll={{ x: 520 }}
            />
            </div>
          </Card>
        </Col>

        {/* Row 12: Forecast Cost at Completion */}
        <Col lg="6">
          <Card className="card-bordered p-3 h-100">
            <h6 className="mb-2">Forecast Cost at Completion</h6>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <AccordionTable
              columns={[
                {
                  title: "Budget",
                  key: "budget",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                },
                {
                  title: "Committed",
                  key: "committed",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 110,
                  render: (_: any, r: any) => {
                    const val = n(r.availableBudget ?? r.available);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Forecast",
                  key: "forecastAtCompletion",
                  width: 110,
                  render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion)),
                },
                {
                  title: "Variance",
                  key: "variance",
                  width: 140,
                  render: (_: any, r: any) => {
                    const val = n(r.Variance ?? r.variance);
                    const pct = r.variancePct != null ? Number(r.variancePct).toFixed(2) : null;
                    return (
                      <span style={{ color: val >= 0 ? "green" : "red" }}>
                        {val >= 0 ? "+" : ""}{formatCr(val)}{pct != null ? ` (${pct}%)` : ""}
                      </span>
                    );
                  },
                },
                {
                  title: "Status",
                  key: "varianceStatus",
                  width: 110,
                  render: (_: any, r: any) => {
                    const s = r.varianceStatus || "";
                    const color = s === "Healthy" ? "green" : s === "Monitor" ? "orange" : s ? "red" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={forecastApiData ? [{ ...forecastApiData, key: 0 }] : []}
              pagination={false}
              size="small"
              scroll={{ x: 720 }}
            />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 13: Budget Control Alerts */}
      <Row className="g-gs mb-3">
        <Col lg="12">
          <Card className="card-bordered p-3">
            <h6 className="mb-2">
              <AlertOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
              Budget Control Alerts
            </h6>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {alertsData.map((alert: any) => {
                const severity = String(alert.alertSeverity ?? alert.severity ?? "").toLowerCase();
                const isError = severity === "critical" || severity === "error" || severity === "high";
                const type = alert.alertType ?? alert.type ?? "";
                const message = alert.alertMessage ?? alert.message ?? "";
                return (
                  <Alert
                    key={alert.key}
                    message={
                      <span>
                        <Tag color={isError ? "red" : "orange"}>{type}</Tag>
                        {message}
                      </span>
                    }
                    type={isError ? "error" : "warning"}
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                );
              })}
            </div>
          </Card>
        </Col>
      </Row>

    </div>
    </Spin>
    );
  };

  // ===================== REPORTS RENDER =====================

  const reportTablePagination = {
    current: reportPagination.page,
    pageSize: reportPagination.pageSize,
    total: reportPagination.total,
    onChange: (p: number, ps: number) => fetchReportData(activeReportTab, p, ps),
  };

  const renderReports = () => (
    <Spin spinning={reportLoading}>
    <div>
      <Tabs activeKey={activeReportTab} onChange={setActiveReportTab} tabPosition="top" type="card">
        {/* Report 1: Project Budget vs Actual */}
        <Tabs.TabPane tab="Budget vs Actual" key="report1">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Project Budget vs Actual Report</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Overall project cost performance against approved budget. Click (+) to drill down: Category → Budget Line → Transaction → Document
            </p>
            <AccordionTable
              columns={[
                {
                  title: "Budget Category",
                  key: "budgetCategory",
                  width: 180,
                  render: (_: any, r: any) => r.budgetCategory || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                },
                {
                  title: "Committed",
                  key: "committed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 130,
                  render: (_: any, r: any) => {
                    const val = n(r.availableBudget ?? r.available);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Forecast",
                  key: "forecast",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion ?? r.forecast)),
                },
                {
                  title: "Variance",
                  key: "variance",
                  width: 140,
                  render: (_: any, r: any) => {
                    const val = n(r.budgetVariance ?? r.variance);
                    return (
                      <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                        {val >= 0 ? "+" : ""}{formatCr(val)}
                      </span>
                    );
                  },
                },
                {
                  title: "Consumed %",
                  key: "consumedPct",
                  width: 150,
                  render: (_: any, r: any) => {
                    const pct = Math.round(n(r.consumedPct));
                    return (
                      <Progress
                        percent={Math.min(pct, 100)}
                        format={() => `${pct}%`}
                        size="small"
                        strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                      />
                    );
                  },
                },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 1100 }}
              summary={() => {
                const totals = reportData.reduce(
                  (acc, r) => ({
                    budget: acc.budget + n(r.totalBudget ?? r.budget),
                    committed: acc.committed + n(r.totalCommitted ?? r.committed),
                    consumed: acc.consumed + n(r.totalConsumed ?? r.consumed),
                    available: acc.available + n(r.availableBudget ?? r.available),
                    forecast: acc.forecast + n(r.forecastAtCompletion ?? r.forecast),
                    variance: acc.variance + n(r.budgetVariance ?? r.variance),
                  }),
                  { budget: 0, committed: 0, consumed: 0, available: 0, forecast: 0, variance: 0 }
                );
                return (
                  <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{formatCr(totals.budget)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{formatCr(totals.committed)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{formatCr(totals.consumed)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}><span style={{ color: totals.available >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(totals.available)}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{formatCr(totals.forecast)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <span style={{ color: totals.variance >= 0 ? "green" : "red" }}>
                        {totals.variance >= 0 ? "+" : ""}{formatCr(totals.variance)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>-</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 2: Budget Consumption by Category */}
        <Tabs.TabPane tab="By Category" key="report2">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget Consumption by Category</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Which categories are consuming the budget the most. Click (+) to drill down: Category → Budget Line → Transaction → Document
            </p>
            <AccordionTable
              columns={[
                {
                  title: "Category",
                  key: "category",
                  width: 180,
                  render: (_: any, r: any) => r.budgetCategory || r.category || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                },
                {
                  title: "Committed",
                  key: "committed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 130,
                  render: (_: any, r: any) => {
                    const val = n(r.availableBudget ?? r.available);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Consumed %",
                  key: "consumedPct",
                  width: 150,
                  render: (_: any, r: any) => {
                    const pct = r.consumedPct != null
                      ? Math.round(n(r.consumedPct))
                      : (() => {
                          const b = n(r.totalBudget ?? r.budget);
                          return b > 0 ? Math.round((n(r.totalConsumed ?? r.consumed) / b) * 100) : 0;
                        })();
                    return (
                      <Progress
                        percent={Math.min(pct, 100)}
                        format={() => `${pct}%`}
                        size="small"
                        strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                      />
                    );
                  },
                },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 900 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const lines = drilldownBudgetLines[record.category] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Budget Lines under {record.category}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "Budget Line", dataIndex: "budgetLine", key: "budgetLine", width: 160 },
                          { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 160 },
                          { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Committed", dataIndex: "committed", key: "committed", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Consumed", dataIndex: "consumed", key: "consumed", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Available", dataIndex: "available", key: "available", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
                        ]}
                        dataSource={lines}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (lineRecord: any) => {
                            const txns = drilldownTransactions[lineRecord.budgetLine] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  Transactions for {lineRecord.budgetLine}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Txn ID", dataIndex: "txnId", key: "txnId", width: 120 },
                                    { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Work Order" ? "purple" : "orange"}>{s}</Tag> },
                                    { title: "Vendor", dataIndex: "vendor", key: "vendor", width: 160 },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : s === "In Progress" ? "blue" : "orange"}>{s}</Tag> },
                                  ]}
                                  dataSource={txns}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                  expandable={{
                                    expandedRowRender: (txnRecord: any) => {
                                      const docs = drilldownDocuments[txnRecord.txnId] || [];
                                      return (
                                        <div style={{ padding: "8px 0" }}>
                                          <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                            Documents for {txnRecord.txnId}
                                          </strong>
                                          <AccordionTable
                                            columns={[
                                              { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                              { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Vendor Invoice" ? "red" : s === "RA Bill" ? "purple" : "cyan"}>{s}</Tag> },
                                              { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                              { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                              { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "blue" : "orange"}>{s}</Tag> },
                                            ]}
                                            dataSource={docs}
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: "max-content" }}
                                          />
                                        </div>
                                      );
                                    },
                                    rowExpandable: (txnRecord: any) => !!drilldownDocuments[txnRecord.txnId],
                                  }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (lineRecord: any) => !!drilldownTransactions[lineRecord.budgetLine],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownBudgetLines[record.category],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 3: Budget Consumption by Contractor */}
        <Tabs.TabPane tab="By Contractor" key="report3">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget Consumption by Contractor</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Helps Contracts Manager evaluate contractor performance. Click (+) to drill down: Contractor → Work Orders → Bills → Payment Documents
            </p>
            <AccordionTable
              columns={[
                { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 180 },
                { title: "Budget Allocated", dataIndex: "budgetAllocated", key: "budgetAllocated", width: 140, render: (v: number) => formatCr(v) },
                { title: "Work Order Value", dataIndex: "woValue", key: "woValue", width: 140, render: (v: number) => formatCr(v) },
                { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 140, render: (v: number) => formatCr(v) },
                { title: "Paid Amount", dataIndex: "paidAmount", key: "paidAmount", width: 130, render: (v: number) => formatCr(v) },
                { title: "Remaining Budget", dataIndex: "remainingBudget", key: "remainingBudget", width: 140, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 870 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const wos = drilldownContractorWOs[record.contractor] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Work Orders for {record.contractor}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "WO ID", dataIndex: "woId", key: "woId", width: 120 },
                          { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 150 },
                          { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 130, render: (v: number) => formatCr(v) },
                          { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                        ]}
                        dataSource={wos}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (woRecord: any) => {
                            const bills = drilldownWOBills[woRecord.woId] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  Bills for {woRecord.woId}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Bill ID", dataIndex: "billId", key: "billId", width: 120 },
                                    { title: "Bill Date", dataIndex: "billDate", key: "billDate", width: 120 },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                  ]}
                                  dataSource={bills}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                  expandable={{
                                    expandedRowRender: (billRecord: any) => {
                                      const docs = drilldownBillDocuments[billRecord.billId] || [];
                                      return (
                                        <div style={{ padding: "8px 0" }}>
                                          <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                            Documents for {billRecord.billId}
                                          </strong>
                                          <AccordionTable
                                            columns={[
                                              { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                              { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Payment Voucher" ? "green" : "orange"}>{s}</Tag> },
                                              { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                              { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                              { title: "Ref No", dataIndex: "refNo", key: "refNo", width: 130 },
                                              { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Applied" ? "cyan" : "orange"}>{s}</Tag> },
                                            ]}
                                            dataSource={docs}
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: "max-content" }}
                                          />
                                        </div>
                                      );
                                    },
                                    rowExpandable: (billRecord: any) => !!drilldownBillDocuments[billRecord.billId],
                                  }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (woRecord: any) => !!drilldownWOBills[woRecord.woId],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownContractorWOs[record.contractor],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 4: Budget vs Purchase Orders */}
        <Tabs.TabPane tab="Budget vs PO" key="report4">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget vs Purchase Orders</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Procurement teams track material budgets vs purchase commitments. Click (+) to drill down: Budget Line → PR → PO → GRN → Invoice
            </p>
            <AccordionTable
              columns={[
                {
                  title: "Budget Line",
                  key: "budgetLine",
                  width: 180,
                  ellipsis: true,
                  render: (_: any, r: any) => r.budgetLineName || r.budgetLine || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)),
                },
                {
                  title: "PR Value",
                  key: "prValue",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.prValue)),
                },
                {
                  title: "PO Value",
                  key: "poValue",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.poValue)),
                },
                {
                  title: "GRN Value",
                  key: "grnValue",
                  width: 120,
                  render: (_: any, r: any) => formatCr(n(r.grnValue)),
                },
                {
                  title: "Invoice Value",
                  key: "invoiceValue",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.invoiceValue)),
                },
                {
                  title: "Remaining",
                  key: "remaining",
                  width: 130,
                  render: (_: any, r: any) => {
                    const val = n(r.remaining);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Status",
                  key: "trafficLight",
                  width: 120,
                  render: (_: any, r: any) => {
                    const s = r.trafficLight || "";
                    const color =
                      s === "Healthy" ? "green" :
                      s === "Monitor" ? "orange" :
                      s === "Risk" ? "red" :
                      s === "No Activity" ? "default" :
                      s ? "blue" : "default";
                    return s ? <Tag color={color}>{s}</Tag> : null;
                  },
                },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 1050 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const prs = drilldownBudgetLinePRs[record.budgetLine] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Purchase Requests for {record.budgetLine}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "PR ID", dataIndex: "prId", key: "prId", width: 110 },
                          { title: "PR Date", dataIndex: "prDate", key: "prDate", width: 110 },
                          { title: "PR Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                          { title: "PO ID", dataIndex: "poId", key: "poId", width: 110 },
                          { title: "PO Value", dataIndex: "poValue", key: "poValue", width: 120, render: (v: number) => formatCr(v) },
                          { title: "GRN Value", dataIndex: "grnValue", key: "grnValue", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Invoice Value", dataIndex: "invoiceValue", key: "invoiceValue", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                        ]}
                        dataSource={prs}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (prRecord: any) => {
                            const docs = drilldownPRDocuments[prRecord.prId] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  PO → GRN → Invoice for {prRecord.prId}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                    { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Goods Receipt" ? "cyan" : "green"}>{s}</Tag> },
                                    { title: "Vendor", dataIndex: "vendor", key: "vendor", width: 150 },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "cyan" : s === "Completed" ? "green" : "orange"}>{s}</Tag> },
                                  ]}
                                  dataSource={docs}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (prRecord: any) => !!drilldownPRDocuments[prRecord.prId],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownBudgetLinePRs[record.budgetLine],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 5: Budget vs Expenses */}
        <Tabs.TabPane tab="Budget vs Expenses" key="report5">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget vs Expenses</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Tracks petty cash and operational expenses against budget. Click (+) to drill down: Expense Category → Expense Entries → Voucher Documents
            </p>
            <AccordionTable
              columns={[
                { title: "Expense Category", dataIndex: "expenseCategory", key: "expenseCategory", width: 160 },
                { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: number) => formatCr(v) },
                { title: "Expense Requests", dataIndex: "expenseRequests", key: "expenseRequests", width: 140, render: (v: number) => formatCr(v) },
                { title: "Approved", dataIndex: "approved", key: "approved", width: 120, render: (v: number) => formatCr(v) },
                { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                { title: "Balance", dataIndex: "balance", key: "balance", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 780 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const entries = drilldownExpenseEntries[record.expenseCategory] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Expense Entries for {record.expenseCategory}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "Expense ID", dataIndex: "expId", key: "expId", width: 110 },
                          { title: "Date", dataIndex: "date", key: "date", width: 110 },
                          { title: "Description", dataIndex: "description", key: "description", width: 180 },
                          { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Approved By", dataIndex: "approvedBy", key: "approvedBy", width: 120 },
                          { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : "blue"}>{s}</Tag> },
                        ]}
                        dataSource={entries}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (entryRecord: any) => {
                            const docs = drilldownExpenseDocuments[entryRecord.expId] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  Voucher for {entryRecord.expId}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Voucher ID", dataIndex: "docId", key: "docId", width: 130 },
                                    { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color="purple">{s}</Tag> },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                    { title: "Payment Mode", dataIndex: "paymentMode", key: "paymentMode", width: 130 },
                                    { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : "blue"}>{s}</Tag> },
                                  ]}
                                  dataSource={docs}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (entryRecord: any) => !!drilldownExpenseDocuments[entryRecord.expId],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownExpenseEntries[record.expenseCategory],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 6: Budget vs Work Orders */}
        <Tabs.TabPane tab="Budget vs WO" key="report6">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget vs Work Orders</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Tracks contractor work package budgets vs execution. Click (+) to drill down: Work Package → Work Order → Bills → Payment Documents
            </p>
            <AccordionTable
              columns={[
                { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 150 },
                { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 120 },
                { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: number) => formatCr(v) },
                { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 130, render: (v: number) => formatCr(v) },
                { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                { title: "Balance", dataIndex: "balance", key: "balance", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 880 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const wos = drilldownWorkPackageWOs[record.workPackage] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Work Orders for {record.workPackage}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "WO ID", dataIndex: "woId", key: "woId", width: 120 },
                          { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 120 },
                          { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                          { title: "RA Bills", dataIndex: "raBills", key: "raBills", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                        ]}
                        dataSource={wos}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (woRecord: any) => {
                            const bills = drilldownWOBills[woRecord.woId] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  Bills for {woRecord.woId}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Bill ID", dataIndex: "billId", key: "billId", width: 120 },
                                    { title: "Bill Date", dataIndex: "billDate", key: "billDate", width: 120 },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                  ]}
                                  dataSource={bills}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                  expandable={{
                                    expandedRowRender: (billRecord: any) => {
                                      const docs = drilldownBillDocuments[billRecord.billId] || [];
                                      return (
                                        <div style={{ padding: "8px 0" }}>
                                          <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                            Documents for {billRecord.billId}
                                          </strong>
                                          <AccordionTable
                                            columns={[
                                              { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                              { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Payment Voucher" ? "green" : "orange"}>{s}</Tag> },
                                              { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                              { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                              { title: "Ref No", dataIndex: "refNo", key: "refNo", width: 130 },
                                              { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Applied" ? "cyan" : "orange"}>{s}</Tag> },
                                            ]}
                                            dataSource={docs}
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: "max-content" }}
                                          />
                                        </div>
                                      );
                                    },
                                    rowExpandable: (billRecord: any) => !!drilldownBillDocuments[billRecord.billId],
                                  }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (woRecord: any) => !!drilldownWOBills[woRecord.woId],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownWorkPackageWOs[record.workPackage],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 7: Budget Utilization Trend */}
        <Tabs.TabPane tab="Utilization Trend" key="report7">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget Utilization Trend</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              How budget is being spent over time. Click (+) to drill down: Month → Transactions → Documents
            </p>
            <div ref={utilizationTrendChartRef} style={{ width: "100%", height: "350px" }} />
            <AccordionTable
              columns={[
                {
                  title: "Month",
                  key: "month",
                  width: 180,
                  render: (_: any, r: any) => {
                    const name = r.fullMonthName || r.monthName || r.month || "";
                    return r.year ? `${name} ${r.year}` : name;
                  },
                },
                {
                  title: "Budget Consumed",
                  key: "consumedValue",
                  width: 180,
                  render: (_: any, r: any) => formatCr(n(r.consumedValue ?? r.budgetConsumed)),
                },
                {
                  title: "Cumulative",
                  key: "cumulativeValue",
                  width: 180,
                  render: (_: any, r: any) => formatCr(n(r.cumulativeValue)),
                },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: "max-content" }}
              style={{ marginTop: 16 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const txns = drilldownMonthTransactions[record.month] || [];
                  return (
                    <div style={{ padding: "8px 0" }}>
                      <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                        Transactions in {record.month}
                      </strong>
                      <AccordionTable
                        columns={[
                          { title: "Txn ID", dataIndex: "txnId", key: "txnId", width: 120 },
                          { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Work Order" ? "purple" : s === "RA Bill" ? "cyan" : "orange"}>{s}</Tag> },
                          { title: "Description", dataIndex: "description", key: "description", width: 200 },
                          { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                          { title: "Date", dataIndex: "date", key: "date", width: 120 },
                        ]}
                        dataSource={txns}
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        expandable={{
                          expandedRowRender: (txnRecord: any) => {
                            const docs = drilldownMonthTxnDocuments[txnRecord.txnId] || [];
                            return (
                              <div style={{ padding: "8px 0" }}>
                                <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                  Documents for {txnRecord.txnId}
                                </strong>
                                <AccordionTable
                                  columns={[
                                    { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                    { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Goods Receipt" ? "cyan" : s === "Vendor Invoice" ? "green" : s === "RA Bill" ? "purple" : s === "Payment Voucher" ? "blue" : "orange"}>{s}</Tag> },
                                    { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                    { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "cyan" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                  ]}
                                  dataSource={docs}
                                  pagination={false}
                                  size="small"
                                  scroll={{ x: "max-content" }}
                                />
                              </div>
                            );
                          },
                          rowExpandable: (txnRecord: any) => !!drilldownMonthTxnDocuments[txnRecord.txnId],
                        }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => !!drilldownMonthTransactions[record.month],
              }}
            />
          </Card>
        </Tabs.TabPane>

        {/* Report 8: Budget Forecast at Completion */}
        <Tabs.TabPane tab="Forecast" key="report8">
          <Card className="card-bordered p-3 mt-2">
            <h6 className="mb-1">Budget Forecast at Completion</h6>
            <p style={{ color: "#8094ae", fontSize: "12px" }}>
              Predicts final project cost — Used by Project Directors. Click (+) to drill down: Category → Budget Line → Transaction → Document
            </p>
            <AccordionTable
              columns={[
                {
                  title: "Category",
                  key: "category",
                  width: 180,
                  render: (_: any, r: any) => r.budgetCategory || r.category || "",
                },
                {
                  title: "Budget",
                  key: "budget",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                },
                {
                  title: "Committed",
                  key: "committed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)),
                },
                {
                  title: "Consumed",
                  key: "consumed",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)),
                },
                {
                  title: "Available",
                  key: "available",
                  width: 130,
                  render: (_: any, r: any) => {
                    const val = n(r.availableBudget ?? r.available);
                    return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                  },
                },
                {
                  title: "Forecast",
                  key: "forecast",
                  width: 130,
                  render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion ?? r.forecast)),
                },
                {
                  title: "Variance",
                  key: "variance",
                  width: 140,
                  render: (_: any, r: any) => {
                    const val = n(r.budgetVariance ?? r.variance);
                    return (
                      <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                        {val >= 0 ? "+" : ""}{formatCr(val)}
                      </span>
                    );
                  },
                },
                {
                  title: "Status",
                  key: "status",
                  width: 120,
                  render: (_: any, r: any) => {
                    const val = n(r.budgetVariance ?? r.variance);
                    return (
                      <Tag color={val >= 0 ? "green" : "red"}>
                        {val >= 0 ? "On Track" : "At Risk"}
                      </Tag>
                    );
                  },
                },
              ]}
              dataSource={reportData}
              pagination={reportTablePagination}
              size="small"
              scroll={{ x: 1150 }}
              summary={() => {
                const totals = reportData.reduce(
                  (acc, r) => ({
                    budget: acc.budget + n(r.totalBudget ?? r.budget),
                    committed: acc.committed + n(r.totalCommitted ?? r.committed),
                    consumed: acc.consumed + n(r.totalConsumed ?? r.consumed),
                    available: acc.available + n(r.availableBudget ?? r.available),
                    forecast: acc.forecast + n(r.forecastAtCompletion ?? r.forecast),
                    variance: acc.variance + n(r.budgetVariance ?? r.variance),
                  }),
                  { budget: 0, committed: 0, consumed: 0, available: 0, forecast: 0, variance: 0 }
                );
                return (
                  <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{formatCr(totals.budget)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{formatCr(totals.committed)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{formatCr(totals.consumed)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <span style={{ color: totals.available >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(totals.available)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{formatCr(totals.forecast)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <span style={{ color: totals.variance >= 0 ? "green" : "red" }}>
                        {totals.variance >= 0 ? "+" : ""}{formatCr(totals.variance)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>
                      <Tag color={totals.variance >= 0 ? "green" : "red"}>
                        {totals.variance >= 0 ? "On Track" : "At Risk"}
                      </Tag>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </Tabs.TabPane>
      </Tabs>
    </div>
    </Spin>
  );

  // ===================== MAIN RENDER =====================

  return (
    <>
      <Head title="Budget Analysis" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">
                Project Budget Control Tower
              </BlockTitle>
              <p style={{ color: "#8094ae", fontSize: "13px" }}>
                {activeBudgetSummary.budgetId} | {activeBudgetSummary.budgetName} | {activeBudgetSummary.siteName} | {activeBudgetSummary.startDate} - {activeBudgetSummary.endDate}
              </p>
            </BlockHeadContent>
            <BlockHeadContent>
              <a
                href="#back"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate(-1);
                }}
                className="btn btn-outline-light bg-white d-none d-sm-inline-flex"
              >
                <Icon name="arrow-left" />
                <span>Back</span>
              </a>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Filters */}
        {renderFilters()}

        {/* Main Tabs */}
        <style>
          {`
            .budget-analysis-tabs .ant-tabs-tab {
              cursor: pointer !important;
            }
            .budget-analysis-tabs .ant-tabs-tab .ant-tabs-tab-btn {
              display: block;
              width: 100%;
              height: 100%;
              cursor: pointer !important;
            }
            .budget-analysis-tabs .ant-tabs-tab .ant-tabs-tab-btn > span {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              width: 100%;
              height: 100%;
              cursor: pointer;
            }
          `}
        </style>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          className="budget-analysis-tabs"
          items={[
            {
              key: "dashboard",
              label: (
                <span>
                  <BarChartOutlined /> Dashboard
                </span>
              ),
              children: renderDashboard(),
            },
            {
              key: "reports",
              label: (
                <span>
                  <FileTextOutlined /> Reports
                </span>
              ),
              children: renderReports(),
            },
          ]}
        />
      </Content>
    </>
  );
};

export default BudgetAnalysisReport;
